import { SheetGeometryGroupId } from "./sheetGeometryGroupId";
import type { ViewportViewType } from "./viewportTypes";

export enum SheetGeometryCategory {
  UNDEFINED = "UNDEFINED",
  RELAXATION_DEBUG = "RELAXATION_DEBUG",

  FOOTING = "FOOTING",
  SLAB = "SLAB",
  FOUNDATION_OUTLINE = "FOUNDATION_OUTLINE",
  SLAB_ABOVE = "SLAB_ABOVE",
  SLAB_BELOW = "SLAB_BELOW",
  SLAB_PATIO = "SLAB_PATIO",
  SLAB_TAG = "SLAB_TAG",

  BRICKLEDGE_CUT = "BRICKLEDGE_CUT",
  BRICKLEDGE_PROJECTED = "BRICKLEDGE_PROJECTED",

  FOUNDATION_CURB = "FOUNDATION_CURB",

  /** For 3D-based views only */
  STAIR = "STAIR",

  STAIR_PROJECTED = "STAIR_PROJECTED",
  STAIR_ABOVE = "STAIR_ABOVE",
  STAIR_BREAK_LINE = "STAIR_BREAK_LINE",

  STEM_WALL_UNIONED_EDGES = "STEM_WALL_UNIONED_EDGES",

  STEM_WALL = "STEM_WALL",
  STEM_WALL_ABOVE = "STEM_WALL_ABOVE",
  STEM_WALL_ABOVE_GAPS = "STEM_WALL_ABOVE_GAPS",
  STEM_WALL_BELOW = "STEM_WALL_BELOW",

  PIER = "PIER",
  PIER_HIDDEN = "PIER_HIDDEN",

  FOUNDATION_DASHED_LINEWORK = "FOUNDATION_DASHED_LINEWORK",
  FOUNDATION_SOLID_LINEWORK = "FOUNDATION_SOLID_LINEWORK",

  UTILITY_FOUNDATION_LINEWORK = "UTILITY_FOUNDATION_LINEWORK",

  ANNOT_LINEWORK = "ANNOT_LINEWORK",
  ANNOT_NOTE = "ANNOT_NOTE",
  ANNOT_DETAIL = "ANNOT_DETAIL",

  ANNOT_DIM_GROUP = "ANNOT_DIM_GROUP",
  ANNOT_DIM_LABEL = "ANNOT_DIM_LABEL",
  ANNOT_DIM_LINE = "ANNOT_DIM_LINE",
  ANNOT_DIM_LINEWORK = "ANNOT_DIM_LINEWORK",
  ANNOT_DIM_TICK = "ANNOT_DIM_TICK",
  ANNOT_DIM_WITNESS = "ANNOT_DIM_WITNESS",

  ALL_LVL_ANNOT_LINEWORK = "ALL_LVL_ANNOT_LINEWORK",
  ALL_LVL_ANNOT_NOTE = "ALL_LVL_ANNOT_NOTE",
  ALL_LVL_ANNOT_DETAIL = "ALL_LVL_ANNOT_DETAIL",

  ALL_LVL_ANNOT_DIM_GROUP = "ALL_LVL_ANNOT_DIM_GROUP",
  ALL_LVL_ANNOT_DIM_LABEL = "ALL_LVL_ANNOT_DIM_LABEL",
  ALL_LVL_ANNOT_DIM_LINE = "ALL_LVL_ANNOT_DIM_LINE",
  ALL_LVL_ANNOT_DIM_LINEWORK = "ALL_LVL_ANNOT_DIM_LINEWORK",
  ALL_LVL_ANNOT_DIM_TICK = "ALL_LVL_ANNOT_DIM_TICK",
  ALL_LVL_ANNOT_DIM_WITNESS = "ALL_LVL_ANNOT_DIM_WITNESS",

  // Elevation level annotations
  TOP_OF_PLATE_MARKERS = "TOP_OF_PLATE_MARKERS",
  TOP_OF_PLATE_DIMENSIONS = "TOP_OF_PLATE_DIMENSIONS",
  DORMER_WINDOW_VERTICAL_DIMENSIONS = "DORMER_WINDOW_VERTICAL_DIMENSIONS",
  DORMER_WINDOW_TAGS = "DORMER_WINDOW_TAGS",
  ROOF_TRUNCATION_HEIGHT_DIMENSIONS = "ROOF_TRUNCATION_HEIGHT_DIMENSIONS",
  ROOF_HORIZONTAL_PLACEMENT_DIMENSIONS = "ROOF_HORIZONTAL_PLACEMENT_DIMENSIONS",
  ROOF_WIDTH_DIMENSIONS = "ROOF_WIDTH_DIMENSIONS",
  ROOF_GABLE_WINDOW_VERTICAL_DIMENSIONS = "ROOF_GABLE_WINDOW_VERTICAL_DIMENSIONS",
  ROOF_GABLE_WINDOW_TAGS = "ROOF_GABLE_WINDOW_TAGS",
  ROOF_DIM_ANNOTATION = "ROOF_DIM_ANNOTATION",
  ROOF_SLOPE_SYMBOL = "ROOF_SLOPE_SYMBOL",
  WINDOW_HEADER_ANNOTATION = "WINDOW_HEADER_ANNOTATION",
  ELEVATION_MARKERS = "ELEVATION_MARKERS",
  MATERIAL_TAGS = "MATERIAL_TAGS",
  GRADE_LINE = "GRADE_LINE",

  ELECTRICAL_CONTROLLERS = "ELECTRICAL_CONTROLLERS",
  ELECTRICAL_LINKS = "ELECTRICAL_LINKS",
  ELECTRICAL_LOADS = "ELECTRICAL_LOADS",
  ELECTRIC_LOAD_LABEL = "ELECTRIC_LOAD_LABEL",

  PROP_FILL = "PROP_FILL",
  PROP_APPLIANCES = "PROP_APPLIANCES",
  PROP_FIXTURES = "PROP_FIXTURES",
  PROP_FURNITURE = "PROP_FURNITURE",
  PROP_OTHER = "PROP_OTHER",
  PROP_FOLIAGE = "PROP_FOLIAGE", // Special - never rendered in linework views to maintain performance, but allowed in realistic style views.

  PROP_TAG = "PROP_TAG",

  COLUMN = "COLUMN",

  DIM_LABEL = "DIM_LABEL",
  DIM_LINE = "DIM_LINE",
  DIM_LINEWORK = "DIM_LINEWORK",
  DIM_TICK = "DIM_TICK",
  DIM_WITNESS = "DIM_WITNESS",
  DIM_GROUP = "DIM_GROUP",

  FOUNDATION_DIM_LABEL = "FOUNDATION_DIM_LABEL",
  FOUNDATION_DIM_LINE = "FOUNDATION_DIM_LINE",
  FOUNDATION_DIM_LINEWORK = "FOUNDATION_DIM_LINEWORK",
  FOUNDATION_DIM_TICK = "FOUNDATION_DIM_TICK",
  FOUNDATION_DIM_WITNESS = "FOUNDATION_DIM_WITNESS",
  FOUNDATION_DIM_GROUP = "FOUNDATION_DIM_GROUP",

  UTILITY_DIM_LABEL = "UTILITY_DIM_LABEL",
  UTILITY_DIM_LINEWORK = "UTILITY_DIM_LINEWORK",
  UTILITY_DIM_TICK = "UTILITY_DIM_TICK",
  UTILITY_DIM_WITNESS = "UTILITY_DIM_WITNESS",

  WALL_INTERIOR_DIM_LABEL = "WALL_INTERIOR_DIM_LABEL",
  WALL_INTERIOR_DIM_LINE = "WALL_INTERIOR_DIM_LINE",
  WALL_INTERIOR_DIM_LINEWORK = "WALL_INTERIOR_DIM_LINEWORK",
  WALL_INTERIOR_DIM_TICK = "WALL_INTERIOR_DIM_TICK",
  WALL_INTERIOR_DIM_WITNESS = "WALL_INTERIOR_DIM_WITNESS",
  WALL_INTERIOR_DIM_GROUP = "WALL_INTERIOR_DIM_GROUP",

  WALL_EXTERIOR_DIM_LABEL = "WALL_EXTERIOR_DIM_LABEL",
  WALL_EXTERIOR_DIM_LINE = "WALL_EXTERIOR_DIM_LINE",
  WALL_EXTERIOR_DIM_LINEWORK = "WALL_EXTERIOR_DIM_LINEWORK",
  WALL_EXTERIOR_DIM_TICK = "WALL_EXTERIOR_DIM_TICK",
  WALL_EXTERIOR_DIM_WITNESS = "WALL_EXTERIOR_DIM_WITNESS",
  WALL_EXTERIOR_DIM_GROUP = "WALL_EXTERIOR_DIM_GROUP",

  ROOF_OVERHANG_DIM_LABEL = "ROOF_OVERHANG_DIM_LABEL",
  ROOF_OVERHANG_DIM_LINE = "ROOF_OVERHANG_DIM_LINE",
  ROOF_OVERHANG_DIM_LINEWORK = "ROOF_OVERHANG_DIM_LINEWORK",
  ROOF_OVERHANG_DIM_TICK = "ROOF_OVERHANG_DIM_TICK",
  ROOF_OVERHANG_DIM_WITNESS = "ROOF_OVERHANG_DIM_WITNESS",
  ROOF_OVERHANG_DIM_GROUP = "ROOF_OVERHANG_DIM_GROUP",

  CONDITION_TAG = "CONDITION_TAG",
  CEILING = "CEILING",

  /** For 3D-based views only */
  RAILING = "RAILING",

  RAILING_PROJECTED = "RAILING_PROJECTED",
  RAILING_ABOVE = "RAILING_ABOVE",
  RAILING_PROJECTED_STAIR_ONLY = "RAILING_PROJECTED_STAIR_ONLY",
  RAILING_ABOVE_STAIR_ONLY = "RAILING_ABOVE_STAIR_ONLY",

  RAILING_POST = "RAILING_POST",
  RAILING_POST_STAIR_ONLY = "RAILING_POST_STAIR_ONLY",

  PORCH_COLUMNS = "PORCH_COLUMNS",
  PORCH_BEAMS = "PORCH_BEAMS",
  PORCH = "PORCH",
  DECK = "DECK",
  PERIMS_WALL = "PERIMS_WALL",
  FLOOR_HEIGHT_DIFFERENCE = "FLOOR_HEIGHT_DIFFERENCE",
  PERIMS_FLOORPLATE = "PERIMS_FLOORPLATE",
  FLOOR_FILL = "FLOOR_FILL",
  FLOOR_AREA_CONDITIONED = "FLOOR_AREA_CONDITIONED",
  FLOOR_AREA_UNCONDITIONED = "FLOOR_AREA_UNCONDITIONED",

  /** Represents walls in elevation, section, and 3D views */
  WALLS = "WALLS",

  // These categories represent walls in plan views
  WALL_BOUNDARIES = "WALL_BOUNDARIES",
  WALL_CORE = "WALL_CORE",
  WALL_SUBSTRATE = "WALL_SUBSTRATE",
  WALL_EXTERIOR_FINISH = "WALL_EXTERIOR_FINISH",
  WALL_EXTERIOR_FINISH_PROJECTED = "WALL_EXTERIOR_FINISH_PROJECTED",

  /**
   * @deprecated used in built in plans to locate stairs in foundation plans.
   * Contains geometry that should be in other categories.
   */
  STAIR_WALLS = "STAIR_WALLS",

  CHASE_ROOMS = "CHASE_ROOMS",

  CASEWORK = "CASEWORK",
  /** @deprecated specific to the built in brochure plan (enables specific style overrides) */
  CASEWORK_SOLID_DASH = "CASEWORK_SOLID_DASH",
  /** @deprecated specific to the built in brochure plan (enables specific style overrides) */
  CASEWORK_POCHE = "CASEWORK_POCHE",

  /** For 3D-based views only */
  WAINSCOTING = "WAINSCOTING",

  WAINSCOTING_CUT = "WAINSCOTING_CUT",
  WAINSCOTING_PROJECTED = "WAINSCOTING_PROJECTED",

  DOORS = "DOORS",
  DOOR_OPERATION_LINEWORK = "DOOR_OPERATION_LINEWORK",
  DOOR_CASING_CUT = "DOOR_CASING_CUT",

  WINDOWS = "WINDOWS",
  WINDOW_CASING_CUT = "WINDOW_CASING_CUT",
  WINDOW_CASING_PROJECTED = "WINDOW_CASING_PROJECTED",

  ROOM_LABELS = "ROOM_LABELS",

  WINDOW_TAGS = "WINDOW_TAGS",
  DOOR_TAGS = "DOOR_TAGS",
  HEADER_FRAMING_TAGS = "HEADER_FRAMING_TAGS",

  CASEWORK_TAG = "CASEWORK_TAG",

  /** @deprecated specific to renderFloorplan for relaxing window and door tags */
  OPENING_TAGS = "OPENING_TAGS",
  /** @deprecated specific to renderFloorplan for relaxing room labels, casework labels, and interior dimensions*/
  INTERIOR_LABELS = "INTERIOR_LABELS",

  ROOF = "ROOF",

  FLOOR_FINISH = "FLOOR_FINISH",
  FLOOR_FRAMING = "FLOOR_FRAMING",
  FLOOR_FRAMING_LABEL = "FLOOR_FRAMING_LABEL",
  FLOOR_SYSTEM_TAG = "FLOOR_SYSTEM_TAG",

  SITE_SHAPE_CONTOURS = "SITE_SHAPE_CONTOURS",
  SITE_SHAPE_ELEVATION_TAGS = "SITE_SHAPE_ELEVATION_TAGS",
  SITE_SHAPE_DRIVEWAYS = "SITE_SHAPE_DRIVEWAYS",
  SITE_SHAPE_ROADS = "SITE_SHAPE_ROADS",

  ROOF_PERIMS = "ROOF_PERIMS",
  ROOF_PERIMS_WITH_LEVEL_PERIMS = "ROOF_PERIMS_WITH_LEVEL_PERIMS",
  ROOF_RIDGE_VENT = "ROOF_RIDGE_VENT",
  ROOF_RIDGE_VENT_ANNOS = "ROOF_RIDGE_VENT_ANNOS",
  ROOF_SLOPE_ANNOS = "ROOF_SLOPE_ANNOS",
  ROOF_SKYLIGHT_ANNOS = "ROOF_SKYLIGHT_ANNOS",
  /**
   * @deprecated This geometry is included in ROOF_PERIMS but not in ROOF_PERIMS_WITH_LEVEL_PERIMS.
   * It should only be used in built in plans.
   */
  ROOF_DORMER_FRAMING = "ROOF_DORMER_FRAMING",
  ROOF_FRAMING = "ROOF_FRAMING",
  FOUNDATION_FOOTING = "FOUNDATION_FOOTING",
  FOUNDATION_PIER = "FOUNDATION_PIER",

  TRIM = "TRIM",

  // Specific trim categories
  ROOF_FRIEZE_BOARD = "ROOF_FRIEZE_BOARD",
  ROOF_FASCIA_BOARD = "ROOF_FASCIA_BOARD",
  ROOF_SOFFIT = "ROOF_SOFFIT",
  ROOF_FASCIA_TOP_TRIM = "ROOF_FASCIA_TOP_TRIM",
  ROOF_BELLY_BAND_BOARD = "ROOF_BELLY_BAND_BOARD",
  ROOF_WALL_BASE_BOARD = "ROOF_WALL_BASE_BOARD",
  ROOF_EXPOSED_RAFTER = "ROOF_EXPOSED_RAFTER",
  ROOF_GABLE_VENT = "ROOF_GABLE_VENT",
  CORNER_BOARD = "CORNER_BOARD",

  WALL_FRAMING = "WALL_FRAMING",
  SUB_FLOORING = "SUB_FLOORING",

  DEBUG_ELEVATION_COLLIDERS = "DEBUG_ELEVATION_COLLIDERS",
  DEBUG_ELEVATION_NOTE_ENTITIES = "DEBUG_ELEVATION_NOTE_ENTITIES",

  SKYLIGHTS = "SKYLIGHTS",

  SECTION_VIEW_CALLOUT = "SECTION_VIEW_CALLOUT",
}

export type SheetGeometryCategoryTable<T> = {
  [key in SheetGeometryCategory]?: T;
};

export const SheetGeometryPropCategories: readonly SheetGeometryCategory[] = Object.freeze([
  SheetGeometryCategory.PROP_APPLIANCES,
  SheetGeometryCategory.PROP_FIXTURES,
  SheetGeometryCategory.PROP_FURNITURE,
  SheetGeometryCategory.PROP_OTHER,
  SheetGeometryCategory.PROP_FOLIAGE,
]);

export const SheetGeometryWainscotingPlanCategories: readonly SheetGeometryCategory[] = Object.freeze([
  SheetGeometryCategory.WAINSCOTING_PROJECTED,
  SheetGeometryCategory.WAINSCOTING_CUT,
]);

export const sheetGeometryRelaxationTargetCategories: readonly SheetGeometryCategory[] = Object.freeze([
  SheetGeometryCategory.ROOM_LABELS,
  SheetGeometryCategory.CASEWORK_TAG,
  SheetGeometryCategory.WINDOW_TAGS,
  SheetGeometryCategory.DOOR_TAGS,
  SheetGeometryCategory.HEADER_FRAMING_TAGS,
  SheetGeometryCategory.SLAB_TAG,
  SheetGeometryCategory.PROP_TAG,
  SheetGeometryCategory.ANNOT_DIM_LABEL,
  SheetGeometryCategory.ANNOT_NOTE,
  SheetGeometryCategory.WALL_EXTERIOR_DIM_LABEL,
  SheetGeometryCategory.WALL_INTERIOR_DIM_LABEL,
  SheetGeometryCategory.FOUNDATION_DIM_LABEL,
]);

export const sheetGeometry3DCategories: readonly SheetGeometryCategory[] = Object.freeze([
  SheetGeometryCategory.CASEWORK,
  SheetGeometryCategory.CEILING,
  SheetGeometryCategory.COLUMN,
  SheetGeometryCategory.DOORS,
  SheetGeometryCategory.FLOOR_FRAMING,
  SheetGeometryCategory.ROOF_FRAMING,
  SheetGeometryCategory.FLOOR_FINISH,
  SheetGeometryCategory.PORCH,
  ...SheetGeometryPropCategories,
  SheetGeometryCategory.RAILING,
  SheetGeometryCategory.ROOF,
  SheetGeometryCategory.ROOF_RIDGE_VENT,
  SheetGeometryCategory.STAIR,
  SheetGeometryCategory.WAINSCOTING,
  SheetGeometryCategory.WALLS,
  SheetGeometryCategory.WINDOWS,
  SheetGeometryCategory.SLAB,
  SheetGeometryCategory.STEM_WALL,
  SheetGeometryCategory.FOUNDATION_CURB,
  SheetGeometryCategory.FOUNDATION_FOOTING,
  SheetGeometryCategory.FOUNDATION_PIER,
  SheetGeometryCategory.ROOF_FRIEZE_BOARD,
  SheetGeometryCategory.ROOF_FASCIA_BOARD,
  SheetGeometryCategory.ROOF_SOFFIT,
  SheetGeometryCategory.ROOF_FASCIA_TOP_TRIM,
  SheetGeometryCategory.ROOF_BELLY_BAND_BOARD,
  SheetGeometryCategory.ROOF_WALL_BASE_BOARD,
  SheetGeometryCategory.ROOF_EXPOSED_RAFTER,
  SheetGeometryCategory.ROOF_GABLE_VENT,
  SheetGeometryCategory.CORNER_BOARD,
  SheetGeometryCategory.WALL_FRAMING,
  SheetGeometryCategory.SUB_FLOORING,
  SheetGeometryCategory.SKYLIGHTS,
  SheetGeometryCategory.TRIM,
]);

export const sheetGeometryCategoriesExcludedFromDefault3DViewTemplates: readonly SheetGeometryCategory[] =
  Object.freeze([
    SheetGeometryCategory.FLOOR_FINISH,
    SheetGeometryCategory.SUB_FLOORING,
    SheetGeometryCategory.WALL_FRAMING,
    SheetGeometryCategory.PROP_FOLIAGE,
  ]);

export const sheetGeometryElevationAnnotationCategories: readonly SheetGeometryCategory[] = Object.freeze([
  // Debug:
  SheetGeometryCategory.DEBUG_ELEVATION_COLLIDERS,
  SheetGeometryCategory.DEBUG_ELEVATION_NOTE_ENTITIES,

  // Automatic:
  SheetGeometryCategory.TOP_OF_PLATE_MARKERS,
  SheetGeometryCategory.TOP_OF_PLATE_DIMENSIONS,
  SheetGeometryCategory.DORMER_WINDOW_VERTICAL_DIMENSIONS,
  SheetGeometryCategory.DORMER_WINDOW_TAGS,
  SheetGeometryCategory.ROOF_HORIZONTAL_PLACEMENT_DIMENSIONS,
  SheetGeometryCategory.ROOF_GABLE_WINDOW_VERTICAL_DIMENSIONS,
  SheetGeometryCategory.ROOF_WIDTH_DIMENSIONS,
  SheetGeometryCategory.ROOF_GABLE_WINDOW_TAGS,
  SheetGeometryCategory.ROOF_DIM_ANNOTATION,
  SheetGeometryCategory.ROOF_SLOPE_SYMBOL,
  SheetGeometryCategory.ROOF_TRUNCATION_HEIGHT_DIMENSIONS,
  SheetGeometryCategory.WINDOW_HEADER_ANNOTATION,
  SheetGeometryCategory.ELEVATION_MARKERS,
  SheetGeometryCategory.MATERIAL_TAGS,
  SheetGeometryCategory.GRADE_LINE,
  SheetGeometryCategory.SECTION_VIEW_CALLOUT,

  // Manual:
  SheetGeometryCategory.ANNOT_DETAIL,
  SheetGeometryCategory.ANNOT_LINEWORK,
  SheetGeometryCategory.ANNOT_NOTE,
  SheetGeometryCategory.ANNOT_DIM_LABEL,
  SheetGeometryCategory.ANNOT_DIM_LINE,
  SheetGeometryCategory.ANNOT_DIM_LINEWORK,
  SheetGeometryCategory.ANNOT_DIM_TICK,
  SheetGeometryCategory.ANNOT_DIM_WITNESS,
]);

const propGeometryCategoryViewTypes: ViewportViewType[] = ["PLAN_VIEW", "ELEVATION_VIEW", "SECTION_VIEW", "VIEW_3D"];

// Categories that can be selected on the View Template UI for a particular view type
export const sheetGeometryCategoryExposedOnUIToViewType: Record<SheetGeometryCategory, ViewportViewType[]> = {
  [SheetGeometryCategory.FOOTING]: ["PLAN_VIEW"],
  [SheetGeometryCategory.SLAB]: ["ELEVATION_VIEW", "SECTION_VIEW", "VIEW_3D"],
  [SheetGeometryCategory.FOUNDATION_OUTLINE]: ["PLAN_VIEW"],
  [SheetGeometryCategory.SLAB_ABOVE]: ["PLAN_VIEW"],
  [SheetGeometryCategory.SLAB_BELOW]: ["PLAN_VIEW"],
  [SheetGeometryCategory.SLAB_PATIO]: ["PLAN_VIEW"],
  [SheetGeometryCategory.SLAB_TAG]: ["PLAN_VIEW"],
  [SheetGeometryCategory.BRICKLEDGE_CUT]: ["PLAN_VIEW"],
  [SheetGeometryCategory.BRICKLEDGE_PROJECTED]: ["PLAN_VIEW"],
  [SheetGeometryCategory.FOUNDATION_CURB]: ["PLAN_VIEW", "ELEVATION_VIEW", "SECTION_VIEW", "VIEW_3D"],
  [SheetGeometryCategory.STAIR]: ["ELEVATION_VIEW", "SECTION_VIEW", "VIEW_3D"],
  [SheetGeometryCategory.STAIR_ABOVE]: ["PLAN_VIEW"],
  [SheetGeometryCategory.STAIR_PROJECTED]: ["PLAN_VIEW"],
  [SheetGeometryCategory.STAIR_BREAK_LINE]: ["PLAN_VIEW"],
  [SheetGeometryCategory.STAIR_WALLS]: ["PLAN_VIEW"],
  [SheetGeometryCategory.STEM_WALL_UNIONED_EDGES]: ["PLAN_VIEW"],
  [SheetGeometryCategory.STEM_WALL]: ["ELEVATION_VIEW", "SECTION_VIEW", "VIEW_3D"],
  [SheetGeometryCategory.STEM_WALL_ABOVE]: ["PLAN_VIEW"],
  [SheetGeometryCategory.STEM_WALL_ABOVE_GAPS]: ["PLAN_VIEW"],
  [SheetGeometryCategory.STEM_WALL_BELOW]: ["PLAN_VIEW"],
  [SheetGeometryCategory.PIER]: ["PLAN_VIEW"],
  [SheetGeometryCategory.PIER_HIDDEN]: ["PLAN_VIEW"],
  [SheetGeometryCategory.FOUNDATION_DASHED_LINEWORK]: ["PLAN_VIEW"],
  [SheetGeometryCategory.FOUNDATION_SOLID_LINEWORK]: ["PLAN_VIEW"],
  [SheetGeometryCategory.SECTION_VIEW_CALLOUT]: ["PLAN_VIEW"],
  [SheetGeometryCategory.ANNOT_DETAIL]: ["PLAN_VIEW", "ELEVATION_VIEW", "SECTION_VIEW", "DRAWING"],
  [SheetGeometryCategory.ANNOT_LINEWORK]: ["PLAN_VIEW", "ELEVATION_VIEW", "SECTION_VIEW", "DRAWING"],
  [SheetGeometryCategory.ANNOT_NOTE]: ["PLAN_VIEW", "ELEVATION_VIEW", "SECTION_VIEW", "DRAWING"],
  [SheetGeometryCategory.ANNOT_DIM_LABEL]: ["PLAN_VIEW", "ELEVATION_VIEW", "SECTION_VIEW", "DRAWING"],
  [SheetGeometryCategory.ANNOT_DIM_LINE]: ["PLAN_VIEW", "ELEVATION_VIEW", "SECTION_VIEW", "DRAWING"],
  [SheetGeometryCategory.ANNOT_DIM_LINEWORK]: ["PLAN_VIEW", "ELEVATION_VIEW", "SECTION_VIEW", "DRAWING"],
  [SheetGeometryCategory.ANNOT_DIM_TICK]: ["PLAN_VIEW", "ELEVATION_VIEW", "SECTION_VIEW", "DRAWING"],
  [SheetGeometryCategory.ANNOT_DIM_WITNESS]: ["PLAN_VIEW", "ELEVATION_VIEW", "SECTION_VIEW", "DRAWING"],
  [SheetGeometryCategory.TOP_OF_PLATE_MARKERS]: ["ELEVATION_VIEW"],
  [SheetGeometryCategory.TOP_OF_PLATE_DIMENSIONS]: ["ELEVATION_VIEW"],
  [SheetGeometryCategory.DORMER_WINDOW_VERTICAL_DIMENSIONS]: ["ELEVATION_VIEW"],
  [SheetGeometryCategory.DORMER_WINDOW_TAGS]: ["ELEVATION_VIEW"],
  [SheetGeometryCategory.ROOF_TRUNCATION_HEIGHT_DIMENSIONS]: ["ELEVATION_VIEW"],
  [SheetGeometryCategory.ROOF_HORIZONTAL_PLACEMENT_DIMENSIONS]: ["ELEVATION_VIEW"],
  [SheetGeometryCategory.ROOF_GABLE_WINDOW_VERTICAL_DIMENSIONS]: ["ELEVATION_VIEW"],
  [SheetGeometryCategory.ROOF_WIDTH_DIMENSIONS]: ["ELEVATION_VIEW"],
  [SheetGeometryCategory.ROOF_GABLE_WINDOW_TAGS]: ["ELEVATION_VIEW"],
  [SheetGeometryCategory.ROOF_DIM_ANNOTATION]: ["ELEVATION_VIEW"],
  [SheetGeometryCategory.ROOF_SLOPE_SYMBOL]: ["ELEVATION_VIEW"],
  [SheetGeometryCategory.ROOF_DORMER_FRAMING]: [],
  [SheetGeometryCategory.WINDOW_HEADER_ANNOTATION]: ["ELEVATION_VIEW"],
  [SheetGeometryCategory.ELEVATION_MARKERS]: ["ELEVATION_VIEW"],
  [SheetGeometryCategory.MATERIAL_TAGS]: ["ELEVATION_VIEW"],
  [SheetGeometryCategory.GRADE_LINE]: ["ELEVATION_VIEW", "SECTION_VIEW"],
  [SheetGeometryCategory.ELECTRICAL_CONTROLLERS]: ["PLAN_VIEW"],
  [SheetGeometryCategory.ELECTRICAL_LINKS]: ["PLAN_VIEW"],
  [SheetGeometryCategory.ELECTRICAL_LOADS]: ["PLAN_VIEW"],
  [SheetGeometryCategory.ELECTRIC_LOAD_LABEL]: ["PLAN_VIEW"],
  [SheetGeometryCategory.PROP_FILL]: ["PLAN_VIEW"],
  [SheetGeometryCategory.PROP_APPLIANCES]: propGeometryCategoryViewTypes,
  [SheetGeometryCategory.PROP_FIXTURES]: propGeometryCategoryViewTypes,
  [SheetGeometryCategory.PROP_FURNITURE]: propGeometryCategoryViewTypes,
  [SheetGeometryCategory.PROP_OTHER]: propGeometryCategoryViewTypes,
  [SheetGeometryCategory.PROP_FOLIAGE]: propGeometryCategoryViewTypes,
  [SheetGeometryCategory.PROP_TAG]: ["PLAN_VIEW"],
  [SheetGeometryCategory.COLUMN]: ["PLAN_VIEW", "ELEVATION_VIEW", "SECTION_VIEW", "VIEW_3D"],
  [SheetGeometryCategory.CONDITION_TAG]: ["PLAN_VIEW"],
  [SheetGeometryCategory.CEILING]: ["PLAN_VIEW", "ELEVATION_VIEW", "SECTION_VIEW", "VIEW_3D"],
  [SheetGeometryCategory.RAILING]: ["ELEVATION_VIEW", "SECTION_VIEW", "VIEW_3D"],
  [SheetGeometryCategory.RAILING_PROJECTED]: ["PLAN_VIEW"],
  [SheetGeometryCategory.RAILING_ABOVE]: ["PLAN_VIEW"],
  [SheetGeometryCategory.RAILING_PROJECTED_STAIR_ONLY]: ["PLAN_VIEW"],
  [SheetGeometryCategory.RAILING_ABOVE_STAIR_ONLY]: ["PLAN_VIEW"],
  [SheetGeometryCategory.RAILING_POST]: ["PLAN_VIEW"],
  [SheetGeometryCategory.RAILING_POST_STAIR_ONLY]: ["PLAN_VIEW"],
  [SheetGeometryCategory.PORCH_COLUMNS]: ["PLAN_VIEW"],
  [SheetGeometryCategory.PORCH_BEAMS]: ["PLAN_VIEW"],
  [SheetGeometryCategory.PORCH]: ["PLAN_VIEW", "ELEVATION_VIEW", "SECTION_VIEW", "VIEW_3D"],
  [SheetGeometryCategory.DECK]: ["PLAN_VIEW"],
  [SheetGeometryCategory.PERIMS_WALL]: ["PLAN_VIEW"],
  [SheetGeometryCategory.FLOOR_HEIGHT_DIFFERENCE]: ["PLAN_VIEW"],
  [SheetGeometryCategory.PERIMS_FLOORPLATE]: ["PLAN_VIEW"],
  [SheetGeometryCategory.FLOOR_FILL]: ["PLAN_VIEW"],
  [SheetGeometryCategory.FLOOR_AREA_CONDITIONED]: ["PLAN_VIEW"],
  [SheetGeometryCategory.FLOOR_AREA_UNCONDITIONED]: ["PLAN_VIEW"],
  [SheetGeometryCategory.FLOOR_FRAMING]: ["PLAN_VIEW", "ELEVATION_VIEW", "SECTION_VIEW", "VIEW_3D"],
  [SheetGeometryCategory.ROOF_FRAMING]: ["PLAN_VIEW", "ELEVATION_VIEW", "SECTION_VIEW", "VIEW_3D"],
  [SheetGeometryCategory.FLOOR_FRAMING_LABEL]: ["PLAN_VIEW"],
  [SheetGeometryCategory.FLOOR_SYSTEM_TAG]: ["PLAN_VIEW"],
  [SheetGeometryCategory.WALLS]: ["ELEVATION_VIEW", "SECTION_VIEW", "VIEW_3D"],
  [SheetGeometryCategory.WALL_BOUNDARIES]: ["PLAN_VIEW"],
  [SheetGeometryCategory.WALL_CORE]: ["PLAN_VIEW"],
  [SheetGeometryCategory.WALL_SUBSTRATE]: ["PLAN_VIEW"],
  [SheetGeometryCategory.WALL_EXTERIOR_FINISH]: ["PLAN_VIEW"],
  [SheetGeometryCategory.WALL_EXTERIOR_FINISH_PROJECTED]: ["PLAN_VIEW"],
  [SheetGeometryCategory.CHASE_ROOMS]: ["PLAN_VIEW"],
  [SheetGeometryCategory.CASEWORK]: ["PLAN_VIEW", "ELEVATION_VIEW", "SECTION_VIEW", "VIEW_3D"],
  [SheetGeometryCategory.DOORS]: ["PLAN_VIEW", "ELEVATION_VIEW", "SECTION_VIEW", "VIEW_3D"],
  [SheetGeometryCategory.DOOR_OPERATION_LINEWORK]: ["PLAN_VIEW"],
  [SheetGeometryCategory.WINDOWS]: ["PLAN_VIEW", "ELEVATION_VIEW", "SECTION_VIEW", "VIEW_3D"],
  [SheetGeometryCategory.WAINSCOTING]: ["ELEVATION_VIEW", "SECTION_VIEW", "VIEW_3D"],
  [SheetGeometryCategory.WAINSCOTING_CUT]: ["PLAN_VIEW"],
  [SheetGeometryCategory.WAINSCOTING_PROJECTED]: ["PLAN_VIEW"],
  [SheetGeometryCategory.DOOR_CASING_CUT]: ["PLAN_VIEW"],
  [SheetGeometryCategory.WINDOW_CASING_CUT]: ["PLAN_VIEW"],
  [SheetGeometryCategory.WINDOW_CASING_PROJECTED]: ["PLAN_VIEW"],
  [SheetGeometryCategory.ROOM_LABELS]: ["PLAN_VIEW", "SECTION_VIEW"],
  [SheetGeometryCategory.WINDOW_TAGS]: ["PLAN_VIEW"],
  [SheetGeometryCategory.DOOR_TAGS]: ["PLAN_VIEW"],
  [SheetGeometryCategory.HEADER_FRAMING_TAGS]: ["PLAN_VIEW"],
  [SheetGeometryCategory.CASEWORK_TAG]: ["PLAN_VIEW"],
  [SheetGeometryCategory.ROOF]: ["PLAN_VIEW", "ELEVATION_VIEW", "SECTION_VIEW", "VIEW_3D"],
  [SheetGeometryCategory.FLOOR_FINISH]: ["PLAN_VIEW", "VIEW_3D"],

  [SheetGeometryCategory.WALL_EXTERIOR_DIM_LABEL]: ["PLAN_VIEW"],
  [SheetGeometryCategory.WALL_EXTERIOR_DIM_LINEWORK]: ["PLAN_VIEW"],
  [SheetGeometryCategory.WALL_EXTERIOR_DIM_TICK]: ["PLAN_VIEW"],
  [SheetGeometryCategory.WALL_EXTERIOR_DIM_WITNESS]: ["PLAN_VIEW"],

  [SheetGeometryCategory.WALL_INTERIOR_DIM_LABEL]: ["PLAN_VIEW"],
  [SheetGeometryCategory.WALL_INTERIOR_DIM_LINEWORK]: ["PLAN_VIEW"],
  [SheetGeometryCategory.WALL_INTERIOR_DIM_TICK]: ["PLAN_VIEW"],
  [SheetGeometryCategory.WALL_INTERIOR_DIM_WITNESS]: ["PLAN_VIEW"],

  [SheetGeometryCategory.FOUNDATION_DIM_LABEL]: ["PLAN_VIEW"],
  [SheetGeometryCategory.FOUNDATION_DIM_LINEWORK]: ["PLAN_VIEW"],
  [SheetGeometryCategory.FOUNDATION_DIM_TICK]: ["PLAN_VIEW"],
  [SheetGeometryCategory.FOUNDATION_DIM_WITNESS]: ["PLAN_VIEW"],

  [SheetGeometryCategory.UTILITY_DIM_LABEL]: ["PLAN_VIEW"],
  [SheetGeometryCategory.UTILITY_DIM_TICK]: ["PLAN_VIEW"],
  [SheetGeometryCategory.UTILITY_DIM_LINEWORK]: ["PLAN_VIEW"],
  [SheetGeometryCategory.UTILITY_DIM_WITNESS]: ["PLAN_VIEW"],

  [SheetGeometryCategory.ROOF_OVERHANG_DIM_LABEL]: ["PLAN_VIEW"],
  [SheetGeometryCategory.ROOF_OVERHANG_DIM_LINEWORK]: ["PLAN_VIEW"],
  [SheetGeometryCategory.ROOF_OVERHANG_DIM_TICK]: ["PLAN_VIEW"],
  [SheetGeometryCategory.ROOF_OVERHANG_DIM_WITNESS]: ["PLAN_VIEW"],

  [SheetGeometryCategory.ROOF_PERIMS]: ["PLAN_VIEW"],
  [SheetGeometryCategory.ROOF_PERIMS_WITH_LEVEL_PERIMS]: ["PLAN_VIEW"],
  [SheetGeometryCategory.ROOF_RIDGE_VENT]: ["PLAN_VIEW", "ELEVATION_VIEW", "SECTION_VIEW", "VIEW_3D"],
  [SheetGeometryCategory.ROOF_RIDGE_VENT_ANNOS]: ["PLAN_VIEW"],
  [SheetGeometryCategory.ROOF_SLOPE_ANNOS]: ["PLAN_VIEW"],
  [SheetGeometryCategory.ROOF_SKYLIGHT_ANNOS]: ["PLAN_VIEW"],

  [SheetGeometryCategory.SITE_SHAPE_CONTOURS]: [], // Disabled for Beta
  [SheetGeometryCategory.SITE_SHAPE_ELEVATION_TAGS]: [], // Disabled for Beta
  [SheetGeometryCategory.SITE_SHAPE_DRIVEWAYS]: [], // Disabled for Beta
  [SheetGeometryCategory.SITE_SHAPE_ROADS]: [], // Disabled for Beta

  [SheetGeometryCategory.FOUNDATION_FOOTING]: ["ELEVATION_VIEW", "SECTION_VIEW", "VIEW_3D"],
  [SheetGeometryCategory.FOUNDATION_PIER]: ["ELEVATION_VIEW", "SECTION_VIEW", "VIEW_3D"],
  [SheetGeometryCategory.TRIM]: ["ELEVATION_VIEW", "SECTION_VIEW", "VIEW_3D"],
  [SheetGeometryCategory.ROOF_FRIEZE_BOARD]: ["ELEVATION_VIEW", "SECTION_VIEW", "VIEW_3D"],
  [SheetGeometryCategory.ROOF_FASCIA_BOARD]: ["ELEVATION_VIEW", "SECTION_VIEW", "VIEW_3D"],
  [SheetGeometryCategory.ROOF_SOFFIT]: ["ELEVATION_VIEW", "SECTION_VIEW", "VIEW_3D"],
  [SheetGeometryCategory.ROOF_FASCIA_TOP_TRIM]: ["ELEVATION_VIEW", "SECTION_VIEW", "VIEW_3D"],
  [SheetGeometryCategory.ROOF_BELLY_BAND_BOARD]: ["ELEVATION_VIEW", "SECTION_VIEW", "VIEW_3D"],
  [SheetGeometryCategory.ROOF_WALL_BASE_BOARD]: ["ELEVATION_VIEW", "SECTION_VIEW", "VIEW_3D"],
  [SheetGeometryCategory.ROOF_EXPOSED_RAFTER]: ["ELEVATION_VIEW", "SECTION_VIEW", "VIEW_3D"],
  [SheetGeometryCategory.ROOF_GABLE_VENT]: ["ELEVATION_VIEW", "SECTION_VIEW", "VIEW_3D"],
  [SheetGeometryCategory.CORNER_BOARD]: ["ELEVATION_VIEW", "SECTION_VIEW", "VIEW_3D"],
  [SheetGeometryCategory.WALL_FRAMING]: ["ELEVATION_VIEW", "SECTION_VIEW", "VIEW_3D"],
  [SheetGeometryCategory.SUB_FLOORING]: ["PLAN_VIEW", "ELEVATION_VIEW", "SECTION_VIEW", "VIEW_3D"],

  [SheetGeometryCategory.SKYLIGHTS]: ["PLAN_VIEW", "ELEVATION_VIEW", "SECTION_VIEW", "VIEW_3D"],

  [SheetGeometryCategory.FOUNDATION_DIM_LINE]: [],
  [SheetGeometryCategory.WALL_INTERIOR_DIM_LINE]: [],
  [SheetGeometryCategory.WALL_EXTERIOR_DIM_LINE]: [],
  [SheetGeometryCategory.ROOF_OVERHANG_DIM_LINE]: [],
  [SheetGeometryCategory.OPENING_TAGS]: [],
  [SheetGeometryCategory.INTERIOR_LABELS]: [],

  [SheetGeometryCategory.UNDEFINED]: [],
  [SheetGeometryCategory.ANNOT_DIM_GROUP]: [],
  [SheetGeometryCategory.FOUNDATION_DIM_GROUP]: [],
  [SheetGeometryCategory.WALL_INTERIOR_DIM_GROUP]: [],
  [SheetGeometryCategory.WALL_EXTERIOR_DIM_GROUP]: [],
  [SheetGeometryCategory.ROOF_OVERHANG_DIM_GROUP]: [],
  [SheetGeometryCategory.DIM_LABEL]: [],
  [SheetGeometryCategory.DIM_LINE]: [],
  [SheetGeometryCategory.DIM_GROUP]: [],
  [SheetGeometryCategory.DIM_LINEWORK]: [],
  [SheetGeometryCategory.DIM_TICK]: [],
  [SheetGeometryCategory.DIM_WITNESS]: [],
  [SheetGeometryCategory.UTILITY_FOUNDATION_LINEWORK]: [],

  [SheetGeometryCategory.ALL_LVL_ANNOT_LINEWORK]: [],
  [SheetGeometryCategory.ALL_LVL_ANNOT_NOTE]: [],
  [SheetGeometryCategory.ALL_LVL_ANNOT_DETAIL]: [],
  [SheetGeometryCategory.ALL_LVL_ANNOT_DIM_GROUP]: [],
  [SheetGeometryCategory.ALL_LVL_ANNOT_DIM_LABEL]: [],
  [SheetGeometryCategory.ALL_LVL_ANNOT_DIM_LINE]: [],
  [SheetGeometryCategory.ALL_LVL_ANNOT_DIM_LINEWORK]: [],
  [SheetGeometryCategory.ALL_LVL_ANNOT_DIM_TICK]: [],
  [SheetGeometryCategory.ALL_LVL_ANNOT_DIM_WITNESS]: [],

  [SheetGeometryCategory.CASEWORK_SOLID_DASH]: [],
  [SheetGeometryCategory.CASEWORK_POCHE]: [],

  [SheetGeometryCategory.DEBUG_ELEVATION_COLLIDERS]: ["ELEVATION_VIEW"],
  [SheetGeometryCategory.DEBUG_ELEVATION_NOTE_ENTITIES]: ["ELEVATION_VIEW"],
  [SheetGeometryCategory.RELAXATION_DEBUG]: ["PLAN_VIEW"],
};

// SheetGeometryCategories are organized into groups identified by a SheetGeometryGroupId.
// SheetGeometryGroups are organized into families identified by a SheetGeometryFamilyId.
export enum SheetGeometryFamilyId {
  Annotation = "Annotation",
  Analytical = "Analytical",
  Building = "Building",
}

export type SheetGeometryFamilyToGroups = { [key in SheetGeometryFamilyId]: SheetGeometryGroupId[] };

export const sheetGeometryFamilyToGroupsMap: SheetGeometryFamilyToGroups = {
  [SheetGeometryFamilyId.Annotation]: [
    SheetGeometryGroupId.AUTO_DIMENSION,
    SheetGeometryGroupId.MANUAL_DIMENSION,
    SheetGeometryGroupId.ANNOTATION,
    SheetGeometryGroupId.CALLOUT,
    SheetGeometryGroupId.TAGS,
    SheetGeometryGroupId.WALL_EXTERIOR_DIMENSIONS,
    SheetGeometryGroupId.WALL_INTERIOR_DIMENSIONS,
    SheetGeometryGroupId.FOUNDATION_DIMENSIONS,
    SheetGeometryGroupId.ROOF_OVERHANG_DIMENSIONS,
    SheetGeometryGroupId.LEVEL_ANNOTATION,
    SheetGeometryGroupId.ROOF_ANNOTATION,
  ],
  [SheetGeometryFamilyId.Analytical]: [
    SheetGeometryGroupId.DEBUG,
    SheetGeometryGroupId.PERIMETER,
    SheetGeometryGroupId.FLOOR_ANALYTICAL,
    // TODO: Support for Key Measures content
  ],
  [SheetGeometryFamilyId.Building]: [
    SheetGeometryGroupId.STAIR,
    SheetGeometryGroupId.ELECTRICAL,
    SheetGeometryGroupId.PROPS,
    SheetGeometryGroupId.COLUMN,
    SheetGeometryGroupId.RAILING,
    SheetGeometryGroupId.PORCH,
    SheetGeometryGroupId.WAINSCOTING,
    SheetGeometryGroupId.WALLS,
    SheetGeometryGroupId.CASEWORK,
    SheetGeometryGroupId.DOORS,
    SheetGeometryGroupId.WINDOWS,
    SheetGeometryGroupId.ROOM,
    SheetGeometryGroupId.ROOF,
    SheetGeometryGroupId.CEILING,
    SheetGeometryGroupId.FLOOR,
    SheetGeometryGroupId.TRIM,
    // Foundation
    SheetGeometryGroupId.SLAB,
    SheetGeometryGroupId.FOUNDATION_PARTS,
    SheetGeometryGroupId.STEM_WALL,
    SheetGeometryGroupId.PIER,
    SheetGeometryGroupId.SITE,
  ],
};

export const viewportCategoryRenderOrder: readonly SheetGeometryCategory[] = [
  SheetGeometryCategory.UNDEFINED,

  SheetGeometryCategory.SITE_SHAPE_CONTOURS,
  SheetGeometryCategory.SITE_SHAPE_DRIVEWAYS,
  SheetGeometryCategory.SITE_SHAPE_ELEVATION_TAGS,
  SheetGeometryCategory.SITE_SHAPE_ROADS,

  SheetGeometryCategory.FOOTING,

  SheetGeometryCategory.SLAB,
  SheetGeometryCategory.SLAB_ABOVE,
  SheetGeometryCategory.SLAB_BELOW,
  SheetGeometryCategory.SLAB_PATIO,
  SheetGeometryCategory.SLAB_TAG,

  SheetGeometryCategory.FOUNDATION_OUTLINE,

  SheetGeometryCategory.BRICKLEDGE_CUT,
  SheetGeometryCategory.BRICKLEDGE_PROJECTED,

  SheetGeometryCategory.FOUNDATION_CURB,
  SheetGeometryCategory.UTILITY_FOUNDATION_LINEWORK,
  SheetGeometryCategory.FOUNDATION_DASHED_LINEWORK,
  SheetGeometryCategory.FOUNDATION_SOLID_LINEWORK,
  SheetGeometryCategory.STEM_WALL_UNIONED_EDGES,
  SheetGeometryCategory.STEM_WALL,
  SheetGeometryCategory.STEM_WALL_ABOVE,
  SheetGeometryCategory.STEM_WALL_ABOVE_GAPS,
  SheetGeometryCategory.STEM_WALL_BELOW,
  SheetGeometryCategory.PIER,
  SheetGeometryCategory.PIER_HIDDEN,
  SheetGeometryCategory.FLOOR_FILL,

  SheetGeometryCategory.PERIMS_WALL,
  SheetGeometryCategory.FLOOR_HEIGHT_DIFFERENCE,
  SheetGeometryCategory.PERIMS_FLOORPLATE,

  SheetGeometryCategory.STAIR,

  SheetGeometryCategory.FLOOR_AREA_CONDITIONED,
  SheetGeometryCategory.FLOOR_AREA_UNCONDITIONED,

  SheetGeometryCategory.PROP_FILL,
  ...SheetGeometryPropCategories,

  SheetGeometryCategory.PROP_TAG,

  SheetGeometryCategory.COLUMN,
  SheetGeometryCategory.CONDITION_TAG,

  SheetGeometryCategory.RAILING,
  SheetGeometryCategory.RAILING_PROJECTED,
  SheetGeometryCategory.RAILING_ABOVE,
  SheetGeometryCategory.RAILING_POST,

  SheetGeometryCategory.PORCH_COLUMNS,
  SheetGeometryCategory.PORCH_BEAMS,
  SheetGeometryCategory.PORCH,
  SheetGeometryCategory.DECK,
  ...SheetGeometryWainscotingPlanCategories,
  SheetGeometryCategory.WALLS,
  SheetGeometryCategory.WALL_CORE,
  SheetGeometryCategory.WALL_SUBSTRATE,
  SheetGeometryCategory.WALL_EXTERIOR_FINISH_PROJECTED,
  SheetGeometryCategory.WALL_EXTERIOR_FINISH,
  SheetGeometryCategory.WALL_BOUNDARIES,

  // Chase rooms rendered after walls to prevent clipping other geometry in the room
  SheetGeometryCategory.CHASE_ROOMS,

  // Stairs should be rendered after walls to ensure that clipping matches 3D view
  SheetGeometryCategory.STAIR_WALLS,
  SheetGeometryCategory.STAIR_PROJECTED,
  SheetGeometryCategory.STAIR_ABOVE,
  SheetGeometryCategory.STAIR_BREAK_LINE,

  // Railings should be rendered after walls and stairs
  SheetGeometryCategory.RAILING_PROJECTED_STAIR_ONLY,
  SheetGeometryCategory.RAILING_ABOVE_STAIR_ONLY,
  SheetGeometryCategory.RAILING_POST_STAIR_ONLY,

  SheetGeometryCategory.ELECTRICAL_CONTROLLERS,
  SheetGeometryCategory.ELECTRICAL_LINKS,
  SheetGeometryCategory.ELECTRICAL_LOADS,
  SheetGeometryCategory.ELECTRIC_LOAD_LABEL,

  SheetGeometryCategory.CASEWORK,
  SheetGeometryCategory.DOOR_OPERATION_LINEWORK,
  SheetGeometryCategory.DOORS,
  SheetGeometryCategory.DOOR_CASING_CUT,
  SheetGeometryCategory.WINDOWS,
  SheetGeometryCategory.SKYLIGHTS,
  SheetGeometryCategory.WINDOW_CASING_PROJECTED,
  SheetGeometryCategory.WINDOW_CASING_CUT,
  SheetGeometryCategory.ROOM_LABELS,
  SheetGeometryCategory.WINDOW_TAGS,
  SheetGeometryCategory.DOOR_TAGS,
  SheetGeometryCategory.HEADER_FRAMING_TAGS,
  SheetGeometryCategory.CASEWORK_TAG,
  SheetGeometryCategory.CEILING,
  SheetGeometryCategory.FLOOR_FRAMING,
  SheetGeometryCategory.ROOF_FRAMING,
  SheetGeometryCategory.FLOOR_FRAMING_LABEL,
  SheetGeometryCategory.FLOOR_SYSTEM_TAG,

  // Placeholder categories, not used in rendering
  SheetGeometryCategory.OPENING_TAGS,
  SheetGeometryCategory.INTERIOR_LABELS,

  // Projected linework categories
  SheetGeometryCategory.ROOF,
  SheetGeometryCategory.ROOF_RIDGE_VENT,
  SheetGeometryCategory.FLOOR_FINISH,
  SheetGeometryCategory.FOUNDATION_FOOTING,
  SheetGeometryCategory.FOUNDATION_PIER,
  SheetGeometryCategory.ROOF_FRIEZE_BOARD,
  SheetGeometryCategory.ROOF_FASCIA_BOARD,
  SheetGeometryCategory.ROOF_SOFFIT,
  SheetGeometryCategory.ROOF_FASCIA_TOP_TRIM,
  SheetGeometryCategory.ROOF_BELLY_BAND_BOARD,
  SheetGeometryCategory.ROOF_WALL_BASE_BOARD,
  SheetGeometryCategory.ROOF_EXPOSED_RAFTER,
  SheetGeometryCategory.ROOF_GABLE_VENT,
  SheetGeometryCategory.CORNER_BOARD,
  SheetGeometryCategory.WALL_FRAMING,
  SheetGeometryCategory.SUB_FLOORING,

  // Elevation annotation categories. Note that these are listed in sheetGeometryElevationAnnotationCategories.
  SheetGeometryCategory.TOP_OF_PLATE_MARKERS,
  SheetGeometryCategory.TOP_OF_PLATE_DIMENSIONS,
  SheetGeometryCategory.ELEVATION_MARKERS,
  SheetGeometryCategory.GRADE_LINE,
  SheetGeometryCategory.DORMER_WINDOW_VERTICAL_DIMENSIONS,
  SheetGeometryCategory.DORMER_WINDOW_TAGS,
  SheetGeometryCategory.ROOF_GABLE_WINDOW_TAGS,
  SheetGeometryCategory.ROOF_DIM_ANNOTATION,
  SheetGeometryCategory.ROOF_SLOPE_SYMBOL,
  SheetGeometryCategory.ROOF_TRUNCATION_HEIGHT_DIMENSIONS,
  SheetGeometryCategory.ROOF_HORIZONTAL_PLACEMENT_DIMENSIONS,
  SheetGeometryCategory.ROOF_GABLE_WINDOW_VERTICAL_DIMENSIONS,
  SheetGeometryCategory.ROOF_WIDTH_DIMENSIONS,
  SheetGeometryCategory.WINDOW_HEADER_ANNOTATION,
  SheetGeometryCategory.MATERIAL_TAGS,

  SheetGeometryCategory.DIM_LABEL,
  SheetGeometryCategory.DIM_LINEWORK,
  SheetGeometryCategory.DIM_TICK,
  SheetGeometryCategory.DIM_WITNESS,

  SheetGeometryCategory.FOUNDATION_DIM_LABEL,
  SheetGeometryCategory.FOUNDATION_DIM_LINEWORK,
  SheetGeometryCategory.FOUNDATION_DIM_TICK,
  SheetGeometryCategory.FOUNDATION_DIM_WITNESS,

  SheetGeometryCategory.WALL_INTERIOR_DIM_LABEL,
  SheetGeometryCategory.WALL_INTERIOR_DIM_LINEWORK,
  SheetGeometryCategory.WALL_INTERIOR_DIM_TICK,
  SheetGeometryCategory.WALL_INTERIOR_DIM_WITNESS,

  SheetGeometryCategory.WALL_EXTERIOR_DIM_LABEL,
  SheetGeometryCategory.WALL_EXTERIOR_DIM_LINEWORK,
  SheetGeometryCategory.WALL_EXTERIOR_DIM_TICK,
  SheetGeometryCategory.WALL_EXTERIOR_DIM_WITNESS,

  SheetGeometryCategory.ROOF_OVERHANG_DIM_LABEL,
  SheetGeometryCategory.ROOF_OVERHANG_DIM_LINEWORK,
  SheetGeometryCategory.ROOF_OVERHANG_DIM_TICK,
  SheetGeometryCategory.ROOF_OVERHANG_DIM_WITNESS,

  SheetGeometryCategory.ROOF_PERIMS,
  SheetGeometryCategory.ROOF_PERIMS_WITH_LEVEL_PERIMS,
  SheetGeometryCategory.ROOF_RIDGE_VENT,
  SheetGeometryCategory.ROOF_RIDGE_VENT_ANNOS,
  SheetGeometryCategory.ROOF_SLOPE_ANNOS,
  SheetGeometryCategory.ROOF_SKYLIGHT_ANNOS,

  SheetGeometryCategory.SECTION_VIEW_CALLOUT,

  SheetGeometryCategory.ANNOT_DETAIL,
  SheetGeometryCategory.ANNOT_LINEWORK,

  SheetGeometryCategory.ANNOT_NOTE,
  SheetGeometryCategory.ANNOT_DIM_LABEL,
  SheetGeometryCategory.ANNOT_DIM_LINEWORK,
  SheetGeometryCategory.ANNOT_DIM_TICK,
  SheetGeometryCategory.ANNOT_DIM_WITNESS,

  SheetGeometryCategory.DEBUG_ELEVATION_COLLIDERS,
  SheetGeometryCategory.DEBUG_ELEVATION_NOTE_ENTITIES,
  SheetGeometryCategory.RELAXATION_DEBUG,
] as const;

export const sheetGeometryCategoryPrettyNames: Record<SheetGeometryCategory, string> = {
  [SheetGeometryCategory.UNDEFINED]: "Undefined",
  [SheetGeometryCategory.FOOTING]: "Footing",
  [SheetGeometryCategory.SLAB]: "Slab",
  [SheetGeometryCategory.FOUNDATION_OUTLINE]: "Foundation Outline",
  [SheetGeometryCategory.SLAB_ABOVE]: "Slab Above",
  [SheetGeometryCategory.SLAB_BELOW]: "Slab Below",
  [SheetGeometryCategory.SLAB_PATIO]: "Patio Slab",
  [SheetGeometryCategory.SLAB_TAG]: "Slab Tags",
  [SheetGeometryCategory.BRICKLEDGE_CUT]: "Brick Ledge Cut",
  [SheetGeometryCategory.BRICKLEDGE_PROJECTED]: "Brick Ledge Projected",
  [SheetGeometryCategory.FOUNDATION_CURB]: "Foundation Curb",
  [SheetGeometryCategory.STAIR]: "Stairs",
  [SheetGeometryCategory.STAIR_ABOVE]: "Stairs Above",
  [SheetGeometryCategory.STAIR_BREAK_LINE]: "Stair Break Line",
  [SheetGeometryCategory.STAIR_PROJECTED]: "Stairs Projected",
  [SheetGeometryCategory.STAIR_WALLS]: "Stair Walls",
  [SheetGeometryCategory.STEM_WALL_UNIONED_EDGES]: "Stem Wall Unified Edges",
  [SheetGeometryCategory.STEM_WALL]: "Stem Wall",
  [SheetGeometryCategory.STEM_WALL_ABOVE]: "Stem Wall Above",
  [SheetGeometryCategory.STEM_WALL_ABOVE_GAPS]: "Stem Wall Above Gaps",
  [SheetGeometryCategory.STEM_WALL_BELOW]: "Stem Wall Below",
  [SheetGeometryCategory.PIER]: "Pier",
  [SheetGeometryCategory.PIER_HIDDEN]: "Pier Hidden",
  [SheetGeometryCategory.FOUNDATION_DASHED_LINEWORK]: "Foundation Dashed Lines",
  [SheetGeometryCategory.FOUNDATION_SOLID_LINEWORK]: "Foundation Solid Lines",
  [SheetGeometryCategory.UTILITY_FOUNDATION_LINEWORK]: "Utility Foundation Linework",
  [SheetGeometryCategory.SECTION_VIEW_CALLOUT]: "Section Callouts",
  [SheetGeometryCategory.ANNOT_DETAIL]: "Annotation Detail",
  [SheetGeometryCategory.ANNOT_LINEWORK]: "Annotation Linework",
  [SheetGeometryCategory.ANNOT_NOTE]: "Annotation Notes",
  [SheetGeometryCategory.ANNOT_DIM_GROUP]: "Annotation Dimension Group",
  [SheetGeometryCategory.ANNOT_DIM_LABEL]: "Annotation Dimension Label",
  [SheetGeometryCategory.ANNOT_DIM_LINE]: "Annotation Dimension Line",
  [SheetGeometryCategory.ANNOT_DIM_LINEWORK]: "Annotation Dimension Linework",
  [SheetGeometryCategory.ANNOT_DIM_TICK]: "Annotation Dimension Tick",
  [SheetGeometryCategory.ANNOT_DIM_WITNESS]: "Annotation Dimension Witness",
  [SheetGeometryCategory.ALL_LVL_ANNOT_DIM_GROUP]: "All Levels Annotation Dimension Group",
  [SheetGeometryCategory.ALL_LVL_ANNOT_DIM_LABEL]: "All Levels Annotation Dimension Label",
  [SheetGeometryCategory.ALL_LVL_ANNOT_DIM_LINE]: "All Levels Annotation Dimension Line",
  [SheetGeometryCategory.ALL_LVL_ANNOT_DIM_LINEWORK]: "All Levels Annotation Dimension Linework",
  [SheetGeometryCategory.ALL_LVL_ANNOT_DIM_TICK]: "All Levels Annotation Dimension Tick",
  [SheetGeometryCategory.ALL_LVL_ANNOT_DIM_WITNESS]: "All Levels Annotation Dimension Witness",
  [SheetGeometryCategory.ALL_LVL_ANNOT_LINEWORK]: "All Levels Annotation Linework",
  [SheetGeometryCategory.ALL_LVL_ANNOT_NOTE]: "All Levels Annotation Notes",
  [SheetGeometryCategory.ALL_LVL_ANNOT_DETAIL]: "All Levels Annotation Detail",
  [SheetGeometryCategory.TOP_OF_PLATE_MARKERS]: "Top of Plate Markers",
  [SheetGeometryCategory.TOP_OF_PLATE_DIMENSIONS]: "Top of Plate Dimensions",
  [SheetGeometryCategory.DORMER_WINDOW_VERTICAL_DIMENSIONS]: "Dormer Window Vertical Dimensions",
  [SheetGeometryCategory.DORMER_WINDOW_TAGS]: "Dormer Window Tags",
  [SheetGeometryCategory.ROOF_TRUNCATION_HEIGHT_DIMENSIONS]: "Roof Truncation Height Dimensions",
  [SheetGeometryCategory.ROOF_HORIZONTAL_PLACEMENT_DIMENSIONS]: "Roof Horizontal Placement Dimensions",
  [SheetGeometryCategory.ROOF_GABLE_WINDOW_VERTICAL_DIMENSIONS]: "Roof Gable Window Vertical Dimensions",
  [SheetGeometryCategory.ROOF_WIDTH_DIMENSIONS]: "Roof Width Dimensions",
  [SheetGeometryCategory.ROOF_GABLE_WINDOW_TAGS]: "Roof Gable Window Tags",
  [SheetGeometryCategory.ROOF_DIM_ANNOTATION]: "Dormer Wall Framing Dimensions",
  [SheetGeometryCategory.ROOF_SLOPE_SYMBOL]: "Roof Slope Symbol",
  [SheetGeometryCategory.ROOF_FRAMING]: "Roof Framing",
  [SheetGeometryCategory.WINDOW_HEADER_ANNOTATION]: "Window Head Height Dimensions",
  [SheetGeometryCategory.ELEVATION_MARKERS]: "Level Datums",
  [SheetGeometryCategory.MATERIAL_TAGS]: "View Notes",
  [SheetGeometryCategory.GRADE_LINE]: "Grade Line",
  [SheetGeometryCategory.ELECTRICAL_CONTROLLERS]: "Electrical Controllers",
  [SheetGeometryCategory.ELECTRICAL_LINKS]: "Electrical Links",
  [SheetGeometryCategory.ELECTRICAL_LOADS]: "Electrical Loads",
  [SheetGeometryCategory.ELECTRIC_LOAD_LABEL]: "Electrical Load Labels",
  [SheetGeometryCategory.PROP_FILL]: "Prop Fill",
  [SheetGeometryCategory.PROP_APPLIANCES]: "Prop Appliances",
  [SheetGeometryCategory.PROP_FIXTURES]: "Prop Fixtures - General",
  [SheetGeometryCategory.PROP_FURNITURE]: "Prop Furniture",
  [SheetGeometryCategory.PROP_OTHER]: "Prop Other",
  [SheetGeometryCategory.PROP_FOLIAGE]: "Prop Foliage",
  [SheetGeometryCategory.PROP_TAG]: "Prop Tags",
  [SheetGeometryCategory.COLUMN]: "Columns",
  [SheetGeometryCategory.DIM_LABEL]: "Dimension Label",
  [SheetGeometryCategory.DIM_LINE]: "Dimension Line",
  [SheetGeometryCategory.DIM_LINEWORK]: "Dimension Linework",
  [SheetGeometryCategory.DIM_TICK]: "Dimension Tick",
  [SheetGeometryCategory.DIM_WITNESS]: "Dimension Witness",
  [SheetGeometryCategory.DIM_GROUP]: "Dimension Group",
  [SheetGeometryCategory.FOUNDATION_DIM_LABEL]: "Foundation Dimension Label",
  [SheetGeometryCategory.FOUNDATION_DIM_LINE]: "Foundation Dimension Line",
  [SheetGeometryCategory.FOUNDATION_DIM_LINEWORK]: "Foundation Dimension Linework",
  [SheetGeometryCategory.FOUNDATION_DIM_TICK]: "Foundation Dimension Tick",
  [SheetGeometryCategory.FOUNDATION_DIM_WITNESS]: "Foundation Dimension Witness",
  [SheetGeometryCategory.FOUNDATION_DIM_GROUP]: "Foundation Dimension Group",
  [SheetGeometryCategory.UTILITY_DIM_LABEL]: "Utility Dimension Labels",
  [SheetGeometryCategory.UTILITY_DIM_LINEWORK]: "Utility Dimension Linework",
  [SheetGeometryCategory.UTILITY_DIM_TICK]: "Utility Dimension Tick",
  [SheetGeometryCategory.UTILITY_DIM_WITNESS]: "Utility Dimension Witness",
  [SheetGeometryCategory.WALL_INTERIOR_DIM_LABEL]: "Interior Wall Dimension Label",
  [SheetGeometryCategory.WALL_INTERIOR_DIM_LINE]: "Interior Wall Dimension Line",
  [SheetGeometryCategory.WALL_INTERIOR_DIM_LINEWORK]: "Interior Wall Dimension Linework",
  [SheetGeometryCategory.WALL_INTERIOR_DIM_TICK]: "Interior Wall Dimension Tick",
  [SheetGeometryCategory.WALL_INTERIOR_DIM_WITNESS]: "Interior Wall Dimension Witness",
  [SheetGeometryCategory.WALL_INTERIOR_DIM_GROUP]: "Interior Wall Dimension Group",
  [SheetGeometryCategory.WALL_EXTERIOR_DIM_LABEL]: "Exterior Wall Dimension Label",
  [SheetGeometryCategory.WALL_EXTERIOR_DIM_LINE]: "Exterior Wall Dimension Line",
  [SheetGeometryCategory.WALL_EXTERIOR_DIM_LINEWORK]: "Exterior Wall Dimension Linework",
  [SheetGeometryCategory.WALL_EXTERIOR_DIM_TICK]: "Exterior Wall Dimension Tick",
  [SheetGeometryCategory.WALL_EXTERIOR_DIM_WITNESS]: "Exterior Wall Dimension Witness",
  [SheetGeometryCategory.WALL_EXTERIOR_DIM_GROUP]: "Exterior Wall Dimension Group",
  [SheetGeometryCategory.ROOF_OVERHANG_DIM_LABEL]: "Roof Overhang Dimension Label",
  [SheetGeometryCategory.ROOF_OVERHANG_DIM_LINEWORK]: "Roof Overhang Dimension Linework",
  [SheetGeometryCategory.ROOF_OVERHANG_DIM_TICK]: "Roof Overhang Dimension Tick",
  [SheetGeometryCategory.ROOF_OVERHANG_DIM_WITNESS]: "Roof Overhang Dimension Witness",
  [SheetGeometryCategory.ROOF_OVERHANG_DIM_GROUP]: "Roof Overhang Dimension Group",
  [SheetGeometryCategory.ROOF_OVERHANG_DIM_LINE]: "Roof Overhang Dimension Line",
  [SheetGeometryCategory.CONDITION_TAG]: "Foundation Condition Tag",
  [SheetGeometryCategory.CEILING]: "Ceiling",
  [SheetGeometryCategory.RAILING]: "Railing",
  [SheetGeometryCategory.RAILING_PROJECTED]: "Railing Projected",
  [SheetGeometryCategory.RAILING_ABOVE]: "Railing Above",
  [SheetGeometryCategory.RAILING_PROJECTED_STAIR_ONLY]: "Stair Railing Projected",
  [SheetGeometryCategory.RAILING_ABOVE_STAIR_ONLY]: "Stair Railing Above",
  [SheetGeometryCategory.RAILING_POST]: "Railing Post",
  [SheetGeometryCategory.RAILING_POST_STAIR_ONLY]: "Stair Railing Post",
  [SheetGeometryCategory.PORCH_COLUMNS]: "Porch Columns",
  [SheetGeometryCategory.PORCH_BEAMS]: "Porch Beams",
  [SheetGeometryCategory.PORCH]: "Porch",
  [SheetGeometryCategory.DECK]: "Deck",
  [SheetGeometryCategory.PERIMS_WALL]: "Perimeter Wall",
  [SheetGeometryCategory.FLOOR_HEIGHT_DIFFERENCE]: "Floor Height Difference",
  [SheetGeometryCategory.PERIMS_FLOORPLATE]: "Perimeter Floorplate",
  [SheetGeometryCategory.FLOOR_FILL]: "Floor Fill",
  [SheetGeometryCategory.FLOOR_AREA_CONDITIONED]: "Floor Area - Conditioned",
  [SheetGeometryCategory.FLOOR_AREA_UNCONDITIONED]: "Floor Area - Unconditioned",
  [SheetGeometryCategory.WALLS]: "Walls",
  [SheetGeometryCategory.WALL_BOUNDARIES]: "Boundary",
  [SheetGeometryCategory.WALL_CORE]: "Core",
  [SheetGeometryCategory.WALL_SUBSTRATE]: "Substrate",
  [SheetGeometryCategory.WALL_EXTERIOR_FINISH]: "Exterior Finish",
  [SheetGeometryCategory.WALL_EXTERIOR_FINISH_PROJECTED]: "Exterior Finish Projected",
  [SheetGeometryCategory.CHASE_ROOMS]: "Chase Rooms",
  [SheetGeometryCategory.CASEWORK]: "Casework",
  [SheetGeometryCategory.CASEWORK_SOLID_DASH]: "Casework Solid Dash",
  [SheetGeometryCategory.CASEWORK_POCHE]: "Casework Poche",
  [SheetGeometryCategory.DOORS]: "Doors",
  [SheetGeometryCategory.DOOR_CASING_CUT]: "Door Casing Cut",
  [SheetGeometryCategory.DOOR_OPERATION_LINEWORK]: "Door Operation Linework",
  [SheetGeometryCategory.WINDOWS]: "Windows",
  [SheetGeometryCategory.WINDOW_CASING_CUT]: "Window Casing Cut",
  [SheetGeometryCategory.WINDOW_CASING_PROJECTED]: "Window Casing Projected",
  [SheetGeometryCategory.WAINSCOTING]: "Wainscoting",
  [SheetGeometryCategory.WAINSCOTING_CUT]: "Wainscoting Cut",
  [SheetGeometryCategory.WAINSCOTING_PROJECTED]: "Wainscoting Projected",
  [SheetGeometryCategory.ROOM_LABELS]: "Room Tags",
  [SheetGeometryCategory.WINDOW_TAGS]: "Window Tags",
  [SheetGeometryCategory.DOOR_TAGS]: "Door Tags",
  [SheetGeometryCategory.HEADER_FRAMING_TAGS]: "Header Framing Tags",
  [SheetGeometryCategory.CASEWORK_TAG]: "Casework Tags",
  [SheetGeometryCategory.OPENING_TAGS]: "Opening Tags",
  [SheetGeometryCategory.INTERIOR_LABELS]: "Interior Labels",
  [SheetGeometryCategory.ROOF]: "Roof",
  [SheetGeometryCategory.FLOOR_FINISH]: "Floor Materials",
  [SheetGeometryCategory.FLOOR_FRAMING]: "Floor Framing",
  [SheetGeometryCategory.FLOOR_FRAMING_LABEL]: "Floor Framing Label",
  [SheetGeometryCategory.FLOOR_SYSTEM_TAG]: "Floor System Tags",
  [SheetGeometryCategory.ROOF_PERIMS]: "Roof Perimeters",
  [SheetGeometryCategory.ROOF_PERIMS_WITH_LEVEL_PERIMS]: "Roof Perimeters with Level Perimeters",
  [SheetGeometryCategory.ROOF_RIDGE_VENT]: "Roof Ridge Vent",
  [SheetGeometryCategory.ROOF_RIDGE_VENT_ANNOS]: "Roof Ridge Vent Annotations",
  [SheetGeometryCategory.ROOF_SLOPE_ANNOS]: "Roof Slope Annotations",
  [SheetGeometryCategory.ROOF_SKYLIGHT_ANNOS]: "Roof Skylight Annotations",
  [SheetGeometryCategory.ROOF_DORMER_FRAMING]: "Roof Dormer Perimeters",
  [SheetGeometryCategory.FOUNDATION_FOOTING]: "Foundation Footing",
  [SheetGeometryCategory.FOUNDATION_PIER]: "Foundation Pier",
  [SheetGeometryCategory.TRIM]: "Trim",
  [SheetGeometryCategory.ROOF_FRIEZE_BOARD]: "Roof Frieze Board",
  [SheetGeometryCategory.ROOF_FASCIA_BOARD]: "Roof Fascia Board",
  [SheetGeometryCategory.ROOF_SOFFIT]: "Roof Soffit",
  [SheetGeometryCategory.ROOF_FASCIA_TOP_TRIM]: "Roof Fascia Top Trim",
  [SheetGeometryCategory.ROOF_BELLY_BAND_BOARD]: "Roof Belly Band Board",
  [SheetGeometryCategory.ROOF_WALL_BASE_BOARD]: "Roof Wall Base Board",
  [SheetGeometryCategory.ROOF_EXPOSED_RAFTER]: "Roof Exposed Rafter",
  [SheetGeometryCategory.ROOF_GABLE_VENT]: "Roof Gable Vent",
  [SheetGeometryCategory.CORNER_BOARD]: "Corner Board",
  [SheetGeometryCategory.WALL_FRAMING]: "Wall Framing",
  [SheetGeometryCategory.SUB_FLOORING]: "Sub Flooring",
  [SheetGeometryCategory.SITE_SHAPE_CONTOURS]: "Site Shape Contours",
  [SheetGeometryCategory.SITE_SHAPE_ELEVATION_TAGS]: "Site Shape Elevation Tags",
  [SheetGeometryCategory.SITE_SHAPE_DRIVEWAYS]: "Site Shape Driveways",
  [SheetGeometryCategory.SITE_SHAPE_ROADS]: "Site Shape Roads",
  [SheetGeometryCategory.DEBUG_ELEVATION_COLLIDERS]: "Debug Elevation Colliders",
  [SheetGeometryCategory.DEBUG_ELEVATION_NOTE_ENTITIES]: "Debug Elevation Note Entities",
  [SheetGeometryCategory.SKYLIGHTS]: "Skylights",
  [SheetGeometryCategory.RELAXATION_DEBUG]: "Relaxation boxes",
};

export enum SheetGeometryStyle {
  LINE = "LINE",
  FILL = "FILL",
  TEXT = "TEXT",
}

export const sheetGeometryStyleMap: Record<SheetGeometryCategory, SheetGeometryStyle | SheetGeometryStyle[]> = {
  [SheetGeometryCategory.UNDEFINED]: SheetGeometryStyle.FILL,
  [SheetGeometryCategory.FOOTING]: SheetGeometryStyle.LINE,
  [SheetGeometryCategory.SLAB]: SheetGeometryStyle.FILL,
  [SheetGeometryCategory.FOUNDATION_OUTLINE]: SheetGeometryStyle.FILL,
  [SheetGeometryCategory.SLAB_ABOVE]: SheetGeometryStyle.FILL,
  [SheetGeometryCategory.SLAB_BELOW]: SheetGeometryStyle.FILL,
  [SheetGeometryCategory.SLAB_PATIO]: SheetGeometryStyle.FILL,
  [SheetGeometryCategory.SLAB_TAG]: SheetGeometryStyle.TEXT,
  [SheetGeometryCategory.BRICKLEDGE_CUT]: SheetGeometryStyle.LINE,
  [SheetGeometryCategory.BRICKLEDGE_PROJECTED]: SheetGeometryStyle.LINE,
  [SheetGeometryCategory.FOUNDATION_CURB]: SheetGeometryStyle.FILL,
  [SheetGeometryCategory.STAIR]: SheetGeometryStyle.FILL,
  [SheetGeometryCategory.STAIR_ABOVE]: SheetGeometryStyle.LINE,
  [SheetGeometryCategory.STAIR_BREAK_LINE]: SheetGeometryStyle.LINE,
  [SheetGeometryCategory.STAIR_PROJECTED]: SheetGeometryStyle.LINE,
  [SheetGeometryCategory.STAIR_WALLS]: SheetGeometryStyle.FILL, // TODO: confirm this is correct
  [SheetGeometryCategory.STEM_WALL_UNIONED_EDGES]: SheetGeometryStyle.LINE,
  [SheetGeometryCategory.STEM_WALL]: SheetGeometryStyle.FILL,
  [SheetGeometryCategory.STEM_WALL_ABOVE]: SheetGeometryStyle.FILL,
  [SheetGeometryCategory.STEM_WALL_ABOVE_GAPS]: SheetGeometryStyle.FILL,
  [SheetGeometryCategory.STEM_WALL_BELOW]: SheetGeometryStyle.FILL,
  [SheetGeometryCategory.PIER]: SheetGeometryStyle.LINE,
  [SheetGeometryCategory.PIER_HIDDEN]: SheetGeometryStyle.LINE,
  [SheetGeometryCategory.FOUNDATION_DASHED_LINEWORK]: SheetGeometryStyle.LINE,
  [SheetGeometryCategory.FOUNDATION_SOLID_LINEWORK]: SheetGeometryStyle.LINE,
  [SheetGeometryCategory.UTILITY_FOUNDATION_LINEWORK]: SheetGeometryStyle.LINE,
  [SheetGeometryCategory.SECTION_VIEW_CALLOUT]: [SheetGeometryStyle.FILL, SheetGeometryStyle.TEXT],
  [SheetGeometryCategory.ANNOT_DETAIL]: SheetGeometryStyle.FILL, // TODO
  [SheetGeometryCategory.ANNOT_LINEWORK]: SheetGeometryStyle.FILL,
  [SheetGeometryCategory.ANNOT_NOTE]: SheetGeometryStyle.TEXT,
  [SheetGeometryCategory.ANNOT_DIM_GROUP]: SheetGeometryStyle.FILL,
  [SheetGeometryCategory.ANNOT_DIM_LABEL]: SheetGeometryStyle.TEXT,
  [SheetGeometryCategory.ANNOT_DIM_LINE]: SheetGeometryStyle.LINE,
  [SheetGeometryCategory.ANNOT_DIM_LINEWORK]: SheetGeometryStyle.LINE,
  [SheetGeometryCategory.ANNOT_DIM_TICK]: SheetGeometryStyle.LINE,
  [SheetGeometryCategory.ANNOT_DIM_WITNESS]: SheetGeometryStyle.LINE,
  [SheetGeometryCategory.TOP_OF_PLATE_MARKERS]: [SheetGeometryStyle.TEXT, SheetGeometryStyle.FILL],
  [SheetGeometryCategory.TOP_OF_PLATE_DIMENSIONS]: [SheetGeometryStyle.LINE, SheetGeometryStyle.TEXT],
  [SheetGeometryCategory.DORMER_WINDOW_VERTICAL_DIMENSIONS]: [SheetGeometryStyle.LINE, SheetGeometryStyle.TEXT],
  [SheetGeometryCategory.DORMER_WINDOW_TAGS]: SheetGeometryStyle.TEXT,
  [SheetGeometryCategory.ROOF_TRUNCATION_HEIGHT_DIMENSIONS]: [SheetGeometryStyle.LINE, SheetGeometryStyle.TEXT],
  [SheetGeometryCategory.ROOF_HORIZONTAL_PLACEMENT_DIMENSIONS]: [SheetGeometryStyle.LINE, SheetGeometryStyle.TEXT],
  [SheetGeometryCategory.ROOF_WIDTH_DIMENSIONS]: [SheetGeometryStyle.LINE, SheetGeometryStyle.TEXT],
  [SheetGeometryCategory.ROOF_GABLE_WINDOW_VERTICAL_DIMENSIONS]: SheetGeometryStyle.FILL,
  [SheetGeometryCategory.ROOF_GABLE_WINDOW_TAGS]: SheetGeometryStyle.TEXT,
  [SheetGeometryCategory.ROOF_DIM_ANNOTATION]: SheetGeometryStyle.TEXT,
  [SheetGeometryCategory.ROOF_SLOPE_SYMBOL]: [SheetGeometryStyle.FILL, SheetGeometryStyle.TEXT],
  [SheetGeometryCategory.WINDOW_HEADER_ANNOTATION]: SheetGeometryStyle.TEXT,
  [SheetGeometryCategory.ELEVATION_MARKERS]: [SheetGeometryStyle.FILL, SheetGeometryStyle.TEXT],
  [SheetGeometryCategory.MATERIAL_TAGS]: [SheetGeometryStyle.TEXT, SheetGeometryStyle.FILL],
  [SheetGeometryCategory.GRADE_LINE]: SheetGeometryStyle.LINE,
  [SheetGeometryCategory.ELECTRICAL_CONTROLLERS]: SheetGeometryStyle.FILL,
  [SheetGeometryCategory.ELECTRICAL_LINKS]: SheetGeometryStyle.LINE,
  [SheetGeometryCategory.ELECTRICAL_LOADS]: SheetGeometryStyle.FILL,
  [SheetGeometryCategory.ELECTRIC_LOAD_LABEL]: SheetGeometryStyle.TEXT,

  [SheetGeometryCategory.PROP_FILL]: SheetGeometryStyle.FILL,
  [SheetGeometryCategory.PROP_APPLIANCES]: SheetGeometryStyle.FILL,
  [SheetGeometryCategory.PROP_FIXTURES]: SheetGeometryStyle.FILL,
  [SheetGeometryCategory.PROP_FURNITURE]: SheetGeometryStyle.FILL,
  [SheetGeometryCategory.PROP_OTHER]: SheetGeometryStyle.FILL,
  [SheetGeometryCategory.PROP_FOLIAGE]: SheetGeometryStyle.FILL,

  [SheetGeometryCategory.PROP_TAG]: SheetGeometryStyle.TEXT,

  [SheetGeometryCategory.COLUMN]: SheetGeometryStyle.FILL,

  [SheetGeometryCategory.DIM_LABEL]: SheetGeometryStyle.TEXT,
  [SheetGeometryCategory.DIM_LINE]: SheetGeometryStyle.LINE,
  [SheetGeometryCategory.DIM_LINEWORK]: SheetGeometryStyle.LINE,
  [SheetGeometryCategory.DIM_TICK]: SheetGeometryStyle.LINE,
  [SheetGeometryCategory.DIM_WITNESS]: SheetGeometryStyle.LINE,
  [SheetGeometryCategory.DIM_GROUP]: SheetGeometryStyle.FILL,

  [SheetGeometryCategory.FOUNDATION_DIM_LABEL]: SheetGeometryStyle.TEXT,
  [SheetGeometryCategory.FOUNDATION_DIM_LINE]: SheetGeometryStyle.LINE,
  [SheetGeometryCategory.FOUNDATION_DIM_LINEWORK]: SheetGeometryStyle.LINE,
  [SheetGeometryCategory.FOUNDATION_DIM_TICK]: SheetGeometryStyle.LINE,
  [SheetGeometryCategory.FOUNDATION_DIM_WITNESS]: SheetGeometryStyle.LINE,
  [SheetGeometryCategory.FOUNDATION_DIM_GROUP]: SheetGeometryStyle.FILL,

  [SheetGeometryCategory.UTILITY_DIM_LABEL]: SheetGeometryStyle.TEXT,
  [SheetGeometryCategory.UTILITY_DIM_LINEWORK]: SheetGeometryStyle.LINE,
  [SheetGeometryCategory.UTILITY_DIM_TICK]: SheetGeometryStyle.LINE,
  [SheetGeometryCategory.UTILITY_DIM_WITNESS]: SheetGeometryStyle.LINE,

  [SheetGeometryCategory.WALL_INTERIOR_DIM_LABEL]: SheetGeometryStyle.TEXT,
  [SheetGeometryCategory.WALL_INTERIOR_DIM_LINE]: SheetGeometryStyle.LINE,
  [SheetGeometryCategory.WALL_INTERIOR_DIM_LINEWORK]: SheetGeometryStyle.LINE,
  [SheetGeometryCategory.WALL_INTERIOR_DIM_TICK]: SheetGeometryStyle.LINE,
  [SheetGeometryCategory.WALL_INTERIOR_DIM_WITNESS]: SheetGeometryStyle.LINE,
  [SheetGeometryCategory.WALL_INTERIOR_DIM_GROUP]: SheetGeometryStyle.FILL,

  [SheetGeometryCategory.WALL_EXTERIOR_DIM_LABEL]: SheetGeometryStyle.TEXT,
  [SheetGeometryCategory.WALL_EXTERIOR_DIM_LINE]: SheetGeometryStyle.LINE,
  [SheetGeometryCategory.WALL_EXTERIOR_DIM_LINEWORK]: SheetGeometryStyle.LINE,
  [SheetGeometryCategory.WALL_EXTERIOR_DIM_TICK]: SheetGeometryStyle.LINE,
  [SheetGeometryCategory.WALL_EXTERIOR_DIM_WITNESS]: SheetGeometryStyle.LINE,
  [SheetGeometryCategory.WALL_EXTERIOR_DIM_GROUP]: SheetGeometryStyle.FILL,

  [SheetGeometryCategory.ROOF_OVERHANG_DIM_LABEL]: SheetGeometryStyle.TEXT,
  [SheetGeometryCategory.ROOF_OVERHANG_DIM_LINE]: SheetGeometryStyle.LINE,
  [SheetGeometryCategory.ROOF_OVERHANG_DIM_LINEWORK]: SheetGeometryStyle.LINE,
  [SheetGeometryCategory.ROOF_OVERHANG_DIM_TICK]: SheetGeometryStyle.LINE,
  [SheetGeometryCategory.ROOF_OVERHANG_DIM_WITNESS]: SheetGeometryStyle.LINE,
  [SheetGeometryCategory.ROOF_OVERHANG_DIM_GROUP]: SheetGeometryStyle.FILL,

  [SheetGeometryCategory.CONDITION_TAG]: [SheetGeometryStyle.TEXT, SheetGeometryStyle.FILL],
  [SheetGeometryCategory.CEILING]: SheetGeometryStyle.FILL,
  [SheetGeometryCategory.RAILING]: SheetGeometryStyle.FILL,
  [SheetGeometryCategory.RAILING_PROJECTED]: SheetGeometryStyle.LINE,
  [SheetGeometryCategory.RAILING_ABOVE]: SheetGeometryStyle.LINE,
  [SheetGeometryCategory.RAILING_PROJECTED_STAIR_ONLY]: SheetGeometryStyle.LINE,
  [SheetGeometryCategory.RAILING_ABOVE_STAIR_ONLY]: SheetGeometryStyle.LINE,
  [SheetGeometryCategory.RAILING_POST]: SheetGeometryStyle.FILL,
  [SheetGeometryCategory.RAILING_POST_STAIR_ONLY]: SheetGeometryStyle.FILL,
  [SheetGeometryCategory.PORCH_COLUMNS]: SheetGeometryStyle.FILL,
  [SheetGeometryCategory.PORCH_BEAMS]: SheetGeometryStyle.FILL,
  [SheetGeometryCategory.PORCH]: SheetGeometryStyle.FILL,
  [SheetGeometryCategory.DECK]: SheetGeometryStyle.FILL,
  [SheetGeometryCategory.PERIMS_WALL]: SheetGeometryStyle.FILL,
  [SheetGeometryCategory.FLOOR_HEIGHT_DIFFERENCE]: SheetGeometryStyle.FILL,
  [SheetGeometryCategory.PERIMS_FLOORPLATE]: SheetGeometryStyle.FILL,
  [SheetGeometryCategory.FLOOR_FILL]: SheetGeometryStyle.FILL,
  [SheetGeometryCategory.FLOOR_AREA_CONDITIONED]: SheetGeometryStyle.FILL,
  [SheetGeometryCategory.FLOOR_AREA_UNCONDITIONED]: SheetGeometryStyle.FILL,
  [SheetGeometryCategory.WALLS]: SheetGeometryStyle.FILL,
  [SheetGeometryCategory.WALL_BOUNDARIES]: SheetGeometryStyle.FILL,
  [SheetGeometryCategory.WALL_CORE]: SheetGeometryStyle.FILL,
  [SheetGeometryCategory.WALL_SUBSTRATE]: SheetGeometryStyle.FILL,
  [SheetGeometryCategory.WALL_EXTERIOR_FINISH]: SheetGeometryStyle.FILL,
  [SheetGeometryCategory.WALL_EXTERIOR_FINISH_PROJECTED]: SheetGeometryStyle.LINE,
  [SheetGeometryCategory.CHASE_ROOMS]: SheetGeometryStyle.FILL,
  [SheetGeometryCategory.CASEWORK]: SheetGeometryStyle.FILL,
  [SheetGeometryCategory.CASEWORK_SOLID_DASH]: SheetGeometryStyle.LINE,
  [SheetGeometryCategory.CASEWORK_POCHE]: SheetGeometryStyle.FILL,
  [SheetGeometryCategory.DOORS]: SheetGeometryStyle.FILL,
  [SheetGeometryCategory.DOOR_CASING_CUT]: SheetGeometryStyle.FILL,
  [SheetGeometryCategory.DOOR_OPERATION_LINEWORK]: SheetGeometryStyle.LINE,
  [SheetGeometryCategory.WINDOWS]: SheetGeometryStyle.FILL,
  [SheetGeometryCategory.WINDOW_CASING_CUT]: SheetGeometryStyle.FILL,
  [SheetGeometryCategory.WINDOW_CASING_PROJECTED]: SheetGeometryStyle.FILL,
  [SheetGeometryCategory.WAINSCOTING]: SheetGeometryStyle.FILL,
  [SheetGeometryCategory.WAINSCOTING_CUT]: SheetGeometryStyle.LINE,
  [SheetGeometryCategory.WAINSCOTING_PROJECTED]: SheetGeometryStyle.LINE,
  [SheetGeometryCategory.ROOM_LABELS]: [SheetGeometryStyle.TEXT, SheetGeometryStyle.LINE],
  [SheetGeometryCategory.WINDOW_TAGS]: SheetGeometryStyle.TEXT,
  [SheetGeometryCategory.DOOR_TAGS]: SheetGeometryStyle.TEXT,
  [SheetGeometryCategory.HEADER_FRAMING_TAGS]: SheetGeometryStyle.TEXT,
  [SheetGeometryCategory.CASEWORK_TAG]: SheetGeometryStyle.TEXT,
  [SheetGeometryCategory.OPENING_TAGS]: SheetGeometryStyle.TEXT,
  [SheetGeometryCategory.INTERIOR_LABELS]: SheetGeometryStyle.TEXT,
  [SheetGeometryCategory.ROOF]: SheetGeometryStyle.LINE,
  [SheetGeometryCategory.FLOOR_FINISH]: SheetGeometryStyle.FILL,
  [SheetGeometryCategory.FLOOR_FRAMING]: SheetGeometryStyle.LINE,
  [SheetGeometryCategory.FLOOR_FRAMING_LABEL]: SheetGeometryStyle.TEXT,
  [SheetGeometryCategory.FLOOR_SYSTEM_TAG]: SheetGeometryStyle.TEXT,
  [SheetGeometryCategory.ROOF_PERIMS]: SheetGeometryStyle.LINE,
  [SheetGeometryCategory.ROOF_PERIMS_WITH_LEVEL_PERIMS]: SheetGeometryStyle.FILL,
  [SheetGeometryCategory.ROOF_RIDGE_VENT]: SheetGeometryStyle.LINE,
  [SheetGeometryCategory.ROOF_RIDGE_VENT_ANNOS]: SheetGeometryStyle.TEXT,
  [SheetGeometryCategory.ROOF_SLOPE_ANNOS]: [SheetGeometryStyle.TEXT, SheetGeometryStyle.FILL],
  [SheetGeometryCategory.ROOF_SKYLIGHT_ANNOS]: SheetGeometryStyle.TEXT,
  [SheetGeometryCategory.ROOF_DORMER_FRAMING]: SheetGeometryStyle.LINE,
  [SheetGeometryCategory.ROOF_FRAMING]: SheetGeometryStyle.LINE,
  [SheetGeometryCategory.ALL_LVL_ANNOT_DIM_GROUP]: SheetGeometryStyle.FILL,
  [SheetGeometryCategory.ALL_LVL_ANNOT_DIM_LABEL]: SheetGeometryStyle.TEXT,
  [SheetGeometryCategory.ALL_LVL_ANNOT_DIM_LINE]: SheetGeometryStyle.LINE,
  [SheetGeometryCategory.ALL_LVL_ANNOT_DIM_LINEWORK]: SheetGeometryStyle.LINE,
  [SheetGeometryCategory.ALL_LVL_ANNOT_DIM_TICK]: SheetGeometryStyle.LINE,
  [SheetGeometryCategory.ALL_LVL_ANNOT_DIM_WITNESS]: SheetGeometryStyle.LINE,
  [SheetGeometryCategory.ALL_LVL_ANNOT_LINEWORK]: SheetGeometryStyle.FILL,
  [SheetGeometryCategory.ALL_LVL_ANNOT_NOTE]: SheetGeometryStyle.TEXT,
  [SheetGeometryCategory.ALL_LVL_ANNOT_DETAIL]: SheetGeometryStyle.FILL,
  [SheetGeometryCategory.FOUNDATION_FOOTING]: SheetGeometryStyle.FILL,
  [SheetGeometryCategory.FOUNDATION_PIER]: SheetGeometryStyle.FILL,
  [SheetGeometryCategory.TRIM]: SheetGeometryStyle.FILL,
  [SheetGeometryCategory.ROOF_FRIEZE_BOARD]: SheetGeometryStyle.FILL,
  [SheetGeometryCategory.ROOF_FASCIA_BOARD]: SheetGeometryStyle.FILL,
  [SheetGeometryCategory.ROOF_SOFFIT]: SheetGeometryStyle.FILL,
  [SheetGeometryCategory.ROOF_FASCIA_TOP_TRIM]: SheetGeometryStyle.FILL,
  [SheetGeometryCategory.ROOF_BELLY_BAND_BOARD]: SheetGeometryStyle.FILL,
  [SheetGeometryCategory.ROOF_WALL_BASE_BOARD]: SheetGeometryStyle.FILL,
  [SheetGeometryCategory.ROOF_EXPOSED_RAFTER]: SheetGeometryStyle.FILL,
  [SheetGeometryCategory.ROOF_GABLE_VENT]: SheetGeometryStyle.FILL,
  [SheetGeometryCategory.CORNER_BOARD]: SheetGeometryStyle.FILL,
  [SheetGeometryCategory.WALL_FRAMING]: SheetGeometryStyle.FILL,
  [SheetGeometryCategory.SUB_FLOORING]: SheetGeometryStyle.FILL,
  [SheetGeometryCategory.SITE_SHAPE_CONTOURS]: SheetGeometryStyle.FILL,
  [SheetGeometryCategory.SITE_SHAPE_ELEVATION_TAGS]: SheetGeometryStyle.TEXT,
  [SheetGeometryCategory.SITE_SHAPE_DRIVEWAYS]: SheetGeometryStyle.FILL,
  [SheetGeometryCategory.SITE_SHAPE_ROADS]: SheetGeometryStyle.FILL,
  [SheetGeometryCategory.DEBUG_ELEVATION_COLLIDERS]: SheetGeometryStyle.FILL,
  [SheetGeometryCategory.DEBUG_ELEVATION_NOTE_ENTITIES]: SheetGeometryStyle.FILL,
  [SheetGeometryCategory.SKYLIGHTS]: SheetGeometryStyle.FILL,
  [SheetGeometryCategory.RELAXATION_DEBUG]: SheetGeometryStyle.FILL,
};

export function isUserDefinedAnnotationCategory(category: SheetGeometryCategory) {
  switch (category) {
    case SheetGeometryCategory.ANNOT_NOTE:
    case SheetGeometryCategory.ANNOT_DETAIL:
    case SheetGeometryCategory.ANNOT_LINEWORK:
    case SheetGeometryCategory.ANNOT_DIM_LABEL:
    case SheetGeometryCategory.ANNOT_DIM_LINEWORK:
    case SheetGeometryCategory.ANNOT_DIM_TICK:
    case SheetGeometryCategory.ANNOT_DIM_WITNESS:
    case SheetGeometryCategory.ALL_LVL_ANNOT_NOTE:
    case SheetGeometryCategory.ALL_LVL_ANNOT_DETAIL:
    case SheetGeometryCategory.ALL_LVL_ANNOT_LINEWORK:
    case SheetGeometryCategory.ALL_LVL_ANNOT_DIM_LABEL:
    case SheetGeometryCategory.ALL_LVL_ANNOT_DIM_LINEWORK:
    case SheetGeometryCategory.ALL_LVL_ANNOT_DIM_TICK:
    case SheetGeometryCategory.ALL_LVL_ANNOT_DIM_WITNESS:
      return true;
    default:
      return false;
  }
}

export function isDormerRelatedSheetGeometryCategory(category: SheetGeometryCategory) {
  switch (category) {
    case SheetGeometryCategory.ROOF_DORMER_FRAMING:
    case SheetGeometryCategory.DORMER_WINDOW_TAGS:
    case SheetGeometryCategory.DORMER_WINDOW_VERTICAL_DIMENSIONS:
    case SheetGeometryCategory.ROOF_DIM_ANNOTATION:
      return true;
    default:
      return false;
  }
}

export const noStyleOverrideSheetGeometryCategories = {
  [SheetGeometryCategory.ELECTRICAL_CONTROLLERS]: ["PLAN_VIEW"],
  [SheetGeometryCategory.ELECTRICAL_LOADS]: ["PLAN_VIEW"],
  [SheetGeometryCategory.FLOOR_FINISH]: ["PLAN_VIEW"],
  [SheetGeometryCategory.CASEWORK]: ["VIEW_3D", "SECTION_VIEW", "ELEVATION_VIEW"],
  [SheetGeometryCategory.COLUMN]: ["SECTION_VIEW"],
  [SheetGeometryCategory.DOORS]: ["SECTION_VIEW"],
  [SheetGeometryCategory.FLOOR_FRAMING]: ["VIEW_3D", "SECTION_VIEW", "ELEVATION_VIEW"],
  [SheetGeometryCategory.ROOF_FRAMING]: ["VIEW_3D", "SECTION_VIEW", "ELEVATION_VIEW"],
  [SheetGeometryCategory.SUB_FLOORING]: ["VIEW_3D", "SECTION_VIEW", "ELEVATION_VIEW"],
  [SheetGeometryCategory.FOUNDATION_CURB]: ["VIEW_3D", "SECTION_VIEW", "ELEVATION_VIEW"],
  [SheetGeometryCategory.FOUNDATION_FOOTING]: ["VIEW_3D", "SECTION_VIEW", "ELEVATION_VIEW"],
  [SheetGeometryCategory.FOUNDATION_PIER]: ["VIEW_3D", "SECTION_VIEW", "ELEVATION_VIEW"],
  [SheetGeometryCategory.SLAB]: ["VIEW_3D", "SECTION_VIEW", "ELEVATION_VIEW"],
  [SheetGeometryCategory.STEM_WALL]: ["VIEW_3D", "SECTION_VIEW", "ELEVATION_VIEW"],
  [SheetGeometryCategory.PORCH]: ["SECTION_VIEW"],
  [SheetGeometryCategory.PROP_APPLIANCES]: ["SECTION_VIEW"],
  [SheetGeometryCategory.PROP_FIXTURES]: ["SECTION_VIEW"],
  [SheetGeometryCategory.PROP_FURNITURE]: ["SECTION_VIEW"],
  [SheetGeometryCategory.PROP_OTHER]: ["SECTION_VIEW"],
  [SheetGeometryCategory.PROP_FOLIAGE]: ["VIEW_3D", "SECTION_VIEW", "ELEVATION_VIEW", "PLAN_VIEW"],
  [SheetGeometryCategory.RAILING]: ["SECTION_VIEW"],
  [SheetGeometryCategory.ROOF]: ["PLAN_VIEW", "SECTION_VIEW"],
  [SheetGeometryCategory.ROOF_RIDGE_VENT]: ["PLAN_VIEW", "SECTION_VIEW"],
  [SheetGeometryCategory.STAIR]: ["SECTION_VIEW"],
  [SheetGeometryCategory.TRIM]: ["SECTION_VIEW"],
  [SheetGeometryCategory.ROOF_FRIEZE_BOARD]: ["SECTION_VIEW"],
  [SheetGeometryCategory.ROOF_FASCIA_BOARD]: ["SECTION_VIEW"],
  [SheetGeometryCategory.ROOF_SOFFIT]: ["SECTION_VIEW"],
  [SheetGeometryCategory.ROOF_FASCIA_TOP_TRIM]: ["SECTION_VIEW"],
  [SheetGeometryCategory.ROOF_BELLY_BAND_BOARD]: ["SECTION_VIEW"],
  [SheetGeometryCategory.ROOF_WALL_BASE_BOARD]: ["SECTION_VIEW"],
  [SheetGeometryCategory.ROOF_EXPOSED_RAFTER]: ["SECTION_VIEW"],
  [SheetGeometryCategory.ROOF_GABLE_VENT]: ["SECTION_VIEW"],
  [SheetGeometryCategory.CORNER_BOARD]: ["SECTION_VIEW"],
  [SheetGeometryCategory.WAINSCOTING]: ["SECTION_VIEW"],
  [SheetGeometryCategory.WALL_FRAMING]: ["VIEW_3D", "SECTION_VIEW", "ELEVATION_VIEW"],
  [SheetGeometryCategory.WALLS]: ["SECTION_VIEW"],
  [SheetGeometryCategory.WINDOWS]: ["SECTION_VIEW"],
  [SheetGeometryCategory.ANNOT_DETAIL]: ["PLAN_VIEW", "ELEVATION_VIEW", "SECTION_VIEW", "VIEW_3D"],
  [SheetGeometryCategory.SKYLIGHTS]: ["PLAN_VIEW", "SECTION_VIEW"],
  [SheetGeometryCategory.RELAXATION_DEBUG]: ["PLAN_VIEW"],
  [SheetGeometryCategory.SECTION_VIEW_CALLOUT]: ["PLAN_VIEW", "ELEVATION_VIEW", "SECTION_VIEW", "VIEW_3D"],
};

export const exteriorDimensionCategoryReplaceMap: SheetGeometryCategoryTable<SheetGeometryCategory> = {
  [SheetGeometryCategory.DIM_GROUP]: SheetGeometryCategory.WALL_EXTERIOR_DIM_GROUP,
  [SheetGeometryCategory.DIM_LINE]: SheetGeometryCategory.WALL_EXTERIOR_DIM_LINE,
  [SheetGeometryCategory.DIM_LINEWORK]: SheetGeometryCategory.WALL_EXTERIOR_DIM_LINEWORK,
  [SheetGeometryCategory.DIM_TICK]: SheetGeometryCategory.WALL_EXTERIOR_DIM_TICK,
  [SheetGeometryCategory.DIM_WITNESS]: SheetGeometryCategory.WALL_EXTERIOR_DIM_WITNESS,
  [SheetGeometryCategory.DIM_LABEL]: SheetGeometryCategory.WALL_EXTERIOR_DIM_LABEL,
};

export const interiorDimensionCategoryReplaceMap: SheetGeometryCategoryTable<SheetGeometryCategory> = {
  [SheetGeometryCategory.DIM_GROUP]: SheetGeometryCategory.WALL_INTERIOR_DIM_GROUP,
  [SheetGeometryCategory.DIM_LINE]: SheetGeometryCategory.WALL_INTERIOR_DIM_LINE,
  [SheetGeometryCategory.DIM_LINEWORK]: SheetGeometryCategory.WALL_INTERIOR_DIM_LINEWORK,
  [SheetGeometryCategory.DIM_TICK]: SheetGeometryCategory.WALL_INTERIOR_DIM_TICK,
  [SheetGeometryCategory.DIM_WITNESS]: SheetGeometryCategory.WALL_INTERIOR_DIM_WITNESS,
  [SheetGeometryCategory.DIM_LABEL]: SheetGeometryCategory.WALL_INTERIOR_DIM_LABEL,
};

export const foundationDimensionCategoryReplaceMap: SheetGeometryCategoryTable<SheetGeometryCategory> = {
  [SheetGeometryCategory.DIM_GROUP]: SheetGeometryCategory.FOUNDATION_DIM_GROUP,
  [SheetGeometryCategory.DIM_LINE]: SheetGeometryCategory.FOUNDATION_DIM_LINE,
  [SheetGeometryCategory.DIM_LINEWORK]: SheetGeometryCategory.FOUNDATION_DIM_LINEWORK,
  [SheetGeometryCategory.DIM_TICK]: SheetGeometryCategory.FOUNDATION_DIM_TICK,
  [SheetGeometryCategory.DIM_WITNESS]: SheetGeometryCategory.FOUNDATION_DIM_WITNESS,
  [SheetGeometryCategory.DIM_LABEL]: SheetGeometryCategory.FOUNDATION_DIM_LABEL,
};

export const utilityDimensionCategoryReplaceMap: SheetGeometryCategoryTable<SheetGeometryCategory> = {
  [SheetGeometryCategory.DIM_LINEWORK]: SheetGeometryCategory.UTILITY_DIM_LINEWORK,
  [SheetGeometryCategory.DIM_TICK]: SheetGeometryCategory.UTILITY_DIM_TICK,
  [SheetGeometryCategory.DIM_WITNESS]: SheetGeometryCategory.UTILITY_DIM_WITNESS,
  [SheetGeometryCategory.DIM_LABEL]: SheetGeometryCategory.UTILITY_DIM_LABEL,
};

export const annotationDimensionCategoryReplaceMap: SheetGeometryCategoryTable<SheetGeometryCategory> = {
  [SheetGeometryCategory.DIM_GROUP]: SheetGeometryCategory.ANNOT_DIM_GROUP,
  [SheetGeometryCategory.DIM_LABEL]: SheetGeometryCategory.ANNOT_DIM_LABEL,
  [SheetGeometryCategory.DIM_LINE]: SheetGeometryCategory.ANNOT_DIM_LINE,
  [SheetGeometryCategory.DIM_LINEWORK]: SheetGeometryCategory.ANNOT_DIM_LINEWORK,
  [SheetGeometryCategory.DIM_TICK]: SheetGeometryCategory.ANNOT_DIM_TICK,
  [SheetGeometryCategory.DIM_WITNESS]: SheetGeometryCategory.ANNOT_DIM_WITNESS,
};

export const annotationAllLvlDimensionCategoryReplaceMap: SheetGeometryCategoryTable<SheetGeometryCategory> = {
  [SheetGeometryCategory.DIM_GROUP]: SheetGeometryCategory.ALL_LVL_ANNOT_DIM_GROUP,
  [SheetGeometryCategory.DIM_LABEL]: SheetGeometryCategory.ALL_LVL_ANNOT_DIM_LABEL,
  [SheetGeometryCategory.DIM_LINE]: SheetGeometryCategory.ALL_LVL_ANNOT_DIM_LINE,
  [SheetGeometryCategory.DIM_LINEWORK]: SheetGeometryCategory.ALL_LVL_ANNOT_DIM_LINEWORK,
  [SheetGeometryCategory.DIM_TICK]: SheetGeometryCategory.ALL_LVL_ANNOT_DIM_TICK,
  [SheetGeometryCategory.DIM_WITNESS]: SheetGeometryCategory.ALL_LVL_ANNOT_DIM_WITNESS,
};

export const roofOverhangDimensionCategoryReplaceMap: SheetGeometryCategoryTable<SheetGeometryCategory> = {
  [SheetGeometryCategory.DIM_LABEL]: SheetGeometryCategory.ROOF_OVERHANG_DIM_LABEL,
  [SheetGeometryCategory.DIM_LINE]: SheetGeometryCategory.ROOF_OVERHANG_DIM_LINE,
  [SheetGeometryCategory.DIM_LINEWORK]: SheetGeometryCategory.ROOF_OVERHANG_DIM_LINEWORK,
  [SheetGeometryCategory.DIM_TICK]: SheetGeometryCategory.ROOF_OVERHANG_DIM_TICK,
  [SheetGeometryCategory.DIM_WITNESS]: SheetGeometryCategory.ROOF_OVERHANG_DIM_WITNESS,
};
